<template>
  <div class="kpmg-rpo-dashboard">
    <!-- Access Control Check -->
    <div v-if="!hasAccess" class="access-denied-container">
      <div class="access-denied-content">
        <div class="access-denied-icon">
          <feather-icon icon="LockIcon" size="48" />
        </div>
        <h3 class="access-denied-title">Access Denied</h3>
        <p class="access-denied-message">
          You don't have permission to access the KPMG RPO Dashboard.
        </p>
        <p class="access-denied-subtitle">
          Required permissions: <code>read kpmg_rpo_dashboard</code> and POC role, or <code>manage kpmg_rpo_dashboard</code>
        </p>
        <b-button variant="primary" @click="$router.go(-1)" class="mt-3">
          <feather-icon icon="ArrowLeftIcon" size="16" class="mr-2" />
          Go Back
        </b-button>
      </div>
    </div>

    <!-- Dashboard Content -->
    <div v-else>
      <!-- Header -->
      <!-- <div class="dashboard-header mb-4">
        <h2 class="text-primary">
          <i class="fas fa-chart-line mr-2"></i>
          KPMG RPO Dashboard
        </h2>
        <p class="text-muted">KPMG RPO candidates overview</p>
      </div> -->

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
        <p class="mt-2">Loading KPMG RPO candidates...</p>
      </div>

      <!-- Dashboard Content -->
      <div v-else>
      <!-- Summary Cards -->
      <b-row class="mb-1">
        <b-col lg="3" md="6" sm="12" class="mb-1">
          <b-card class="metric-card primary-gradient">
            <div class="metric-content">
              <div class="metric-info">
                <h3 class="metric-value">{{ totalCandidates }}</h3>
                <p class="metric-label">Total Candidates</p>
              </div>
              <div class="metric-icon">
                <feather-icon icon="UsersIcon" size="24" />
              </div>
            </div>
          </b-card>
        </b-col>

        <b-col lg="3" md="6" sm="12" class="mb-1">
          <b-card class="metric-card success-gradient">
            <div class="metric-content">
              <div class="metric-info">
                <h3 class="metric-value">{{ joinedCount }}</h3>
                <p class="metric-label">Joined</p>
              </div>
              <div class="metric-icon">
                <feather-icon icon="UserCheckIcon" size="24" />
              </div>
            </div>
          </b-card>
        </b-col>

        <b-col lg="3" md="6" sm="12" class="mb-1">
          <b-card class="metric-card info-gradient">
            <div class="metric-content">
              <div class="metric-info">
                <h3 class="metric-value">{{ offeredCount }}</h3>
                <p class="metric-label">Offered</p>
              </div>
              <div class="metric-icon">
                <feather-icon icon="UserPlusIcon" size="24" />
              </div>
            </div>
          </b-card>
        </b-col>

        <b-col lg="3" md="6" sm="12" class="mb-1">
          <b-card class="metric-card warning-gradient">
            <div class="metric-content">
              <div class="metric-info">
                <h3 class="metric-value">{{ shortlistedCount }}</h3>
                <p class="metric-label">Shortlisted</p>
              </div>
              <div class="metric-icon">
                <feather-icon icon="UserIcon" size="24" />
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>





      <!-- Candidates Table -->
      <b-row>
        <b-col cols="12" class="mb-3">
          <b-card title="KPMG RPO Candidates" class="candidates-card">
                        <!-- Bulk Actions -->
            <div v-if="selectedCandidates.length > 0 && canAssignBusinessAndPOC" class="bulk-actions mb-3">
              <div class="d-flex align-items-center">
                <div class="selection-indicator">
                  <feather-icon icon="CheckCircleIcon" size="18" class="text-success mr-2" />
                  <span class="selection-count">{{ selectedCandidates.length }} candidates selected</span>
                </div>
                <b-button size="sm" variant="primary" @click="openBulkAssignmentModal" class="ml-3">
                  <feather-icon icon="UsersIcon" size="16" class="mr-1" />
                  Bulk Assign
                </b-button>
                <b-button size="sm" variant="outline-secondary" @click="clearSelection" class="ml-2">
                  Clear Selection
                </b-button>
              </div>
            </div>

            <!-- Table Controls -->
            <div class="table-controls mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                  <span class="mr-2">Entries:</span>
                  <b-form-select
                    v-model="perPage"
                    :options="[25, 50, 75, 100]"
                    size="sm"
                    style="width: 80px;"
                    @change="onPageSizeChange"
                  />
                </div>
                <div class="d-flex align-items-center">
                  <div class="search-container mr-2">
                    <div class="input-group">
                      <b-form-input
                        v-model="filters.search"
                        placeholder="Search candidates (min 4 characters)..."
                        size="sm"
                        style="width: 180px;"
                        :class="{ 'is-invalid': filters.search.length > 0 && filters.search.length < 4 }"
                        @input="onSearchInput"
                        @keyup.enter="onSearchInput"
                      />
                      <div class="input-group-append">
                        <b-button 
                          v-if="filters.search.length >= 4"
                          size="sm" 
                          variant="outline-danger"
                          @click="clearSearch"
                          title="Clear search"
                        >
                          <feather-icon icon="XIcon" size="16" />
                        </b-button>
                        <b-button 
                          size="sm" 
                          variant="outline-primary"
                          @click="onSearchInput"
                          :disabled="filters.search.length > 0 && filters.search.length < 4"
                          title="Search"
                        >
                          <feather-icon icon="SearchIcon" size="16" />
                        </b-button>
                      </div>
                      <span v-if="filters.search.length > 0 && filters.search.length < 4" class="text-danger"> Please enter at least 4 characters to search</span> 
                    </div>
                    <!-- <div v-if="filters.search.length > 0" class="search-feedback">
                      <small :class="filters.search.length >= 4 ? 'text-success' : 'text-warning'">
                        {{ filters.search.length }}/4 characters
                      </small>
                    </div> -->
                  </div>
                  
                  <v-select
                    v-model="filters.status"
                    :options="statusOptions"
                    multiple
                    clearable
                    label="text"
                    :reduce="option => option.value"
                    :close-on-select="false"
                    :searchable="true"
                    class="mr-2"
                    style="width: 200px;"
                    @input="loadCandidates"
                    placeholder="Filter by status"
                  />
                  <b-form-select
                    v-model="filters.business"
                    :options="businessFilterOptions"
                    size="sm"
                    style="width: 150px;"
                    class="mr-2"
                    @change="loadCandidates"
                  />
                  <b-form-select
                    v-model="filters.poc"
                    :options="pocFilterOptions"
                    size="sm"
                    style="width: 150px;"
                    class="mr-2"
                    @change="loadCandidates"
                  />
                  <b-button 
                    size="sm" 
                    variant="outline-secondary"
                    @click="clearFilters"
                    title="Clear all filters"
                  >
                    <feather-icon icon="XIcon" size="16" />
                  </b-button>
                </div>
              </div>
            </div>

            <!-- Table Header with Selection Info -->
            <div class="table-header-info mb-2">
              <div class="d-flex justify-content-between align-items-center">
                <div class="selection-info">
                  <feather-icon icon="CheckSquareIcon" size="16" class="text-primary mr-2" />
                  <span class="text-muted" v-if="canAssignBusinessAndPOC">
                    Select candidates using checkboxes in the table below for bulk assignment
                  </span>
                  <span class="text-muted" v-else>
                    Viewing candidates assigned to you
                  </span>
                </div>
                <div class="table-stats">
                  <small class="text-muted">Showing {{ assignedCandidates.length }} of {{ paginationInfo.totalRecords }} candidates</small>
                </div>
              </div>
            </div>

            <div class="table-responsive">
              <b-table
                ref="candidatesTable"
                :key="tableKey"
                :items="assignedCandidates"
                :fields="filteredTableFields"
                :busy="loading"
                
                hover
                responsive
                :selectable="canAssignBusinessAndPOC"
                select-mode="multi"
                @row-selected="onRowSelected"
                class="candidates-table"
                :select-head="canAssignBusinessAndPOC"
                select-variant="primary"
                select-head-variant="primary"
                show-empty
                empty-text="No candidates found"
                empty-filtered-text="No candidates match your filters"
              >
                                  <template #table-busy>
                    <div class="text-center my-2">
                      <b-spinner class="align-middle"></b-spinner>
                      <strong>Loading...</strong>
                    </div>
                  </template>

                  <template #head(selection) v-if="canAssignBusinessAndPOC">
                    <b-form-checkbox
                      v-model="selectAll"
                      plain
                      @change="toggleSelectAll"
                    />
                  </template>

                  <template #cell(selection)="row" v-if="canAssignBusinessAndPOC">
                    <b-form-checkbox
                      v-model="row.item.selected"
                      plain
                      @change="toggleSelection(row.item)"
                    />
                  </template>

                <template #cell(candidate_name)="data">
                  <router-link 
                    v-if="$can('read','candidates')"
                    :to="{
                      name: 'apps-candidates-view',
                      params: { id: data.item.cid, jobId: data.item.jid },
                      query: { 
                        job_id: data.item.job_id,
                        position_name: data.item.position_name ? data.item.position_name.replace(data.item.job_id + ' - ','') : '',
                        client: data.item.client,
                        attachments: data.item.attachments,
                        recruiter: data.item.rec_id,
                        locations: data.item.locations,
                        sourcer: data.item.sourcer,
                        am_name: data.item.account_mgr,
                        position_type: data.item.position_type,
                        vertical: data.item.vertical,
                        category: data.item.category,
                        payroll: data.item.payroll
                      }
                    }"
                  >
                    <div class="candidate-info">
                      
                      <div>
                        <div class="font-weight-bold candidate-name" v-b-tooltip.hover :title="getCandidateName(data.item)">
                          {{ getCandidateName(data.item) }}
                        </div>
                        <small class="text-muted candidate-info">{{ data.item.email }}</small>
                      </div>
                    </div>
                  </router-link>
                  <div v-else class="candidate-info">
                    
                    <div>
                      <div class="font-weight-bold candidate-name" v-b-tooltip.hover :title="getCandidateName(data.item)">
                        {{ getCandidateName(data.item) }}
                      </div>
                      <small class="text-muted candidate-info">{{ data.item.email }}</small>
                    </div>
                  </div>
                </template>

                <template #cell(candidateStatus)="data">
                  <div class="status-indicator">
                    <b-badge :variant="getStatusVariant(data.item.candidateStatus)">
                      {{ data.item.candidateStatus }}
                    </b-badge>
                  </div>
                </template>

                <template #cell(position_name)="data">
                  <router-link :to="'/apps/view-job/' + data.item.jid">
                      <div class="position-info" v-b-tooltip.hover :title="getPositionName(data.item)">
                        <div class="position-name">{{ getPositionName(data.item) }}</div>
                        <div class="position-details">
                          <span v-if="data.item.position_type === 'Contractual'" class="badge badge-warning mr-1">Contractual</span>
                          <small class="text-muted">{{ data.item.client }}</small>
                        </div>
                      </div>
                    </router-link>
                </template>

                <template #cell(assignedTo)="data">
                  <span>{{ getAssignedPOCName(data.item) }}</span>
                </template>

                <template #cell(offerProcessStage)="data">
                  <div class="offer-process-stage-info">
                    <span 
                      v-b-tooltip.hover 
                      :title="getOfferProcessTooltip(data.item)"
                      class="stage-status"
                    >
                      {{ getCurrentOfferProcessStatus(data.item) }}
                    </span>
                    <small v-if="getLastUpdatedAt(data.item)" class="last-updated">
                      {{ getLastUpdatedAt(data.item) }}
                    </small>
                  </div>
                </template>

                <template #cell(actions)="data">
                  <div class="action-buttons">
                    <b-button 
                      v-if="canAssignBusinessAndPOC"
                      size="sm" 
                      variant="outline-primary"
                      @click="assignBusinessAndPOC(data.item)"
                    >
                      <!-- <feather-icon icon="SettingsIcon" size="16" class="mr-1" /> -->
                      {{ getAssignedPOCName(data.item) !== 'N/A' ? 'Reassign' : 'Assign' }}
                    </b-button>
                    <span v-else class="text-muted">
                      <small>Read Only</small>
                    </span>
                  </div>
                </template>
              </b-table>
            </div>

            <!-- Pagination -->
            <div class="d-flex justify-content-between align-items-center mt-3">
              <div>
                <span v-if="paginationInfo.totalRecords > 0">
                  Showing {{ (paginationInfo.currentPage - 1) * paginationInfo.limit + 1 }} to {{ Math.min(paginationInfo.currentPage * paginationInfo.limit, paginationInfo.totalRecords) }} of {{ paginationInfo.totalRecords }} candidates
                </span>
                <span v-else>
                  No candidates found
                </span>
              </div>
              <div class="d-flex align-items-center">
                <span class="mr-3 text-muted">
                  Page {{ paginationInfo.currentPage }} of {{ paginationInfo.totalPages }}
                </span>
              <b-pagination
                v-model="currentPage"
                  :total-rows="paginationInfo.totalRecords"
                  :per-page="paginationInfo.limit"
                  :number-of-pages="paginationInfo.totalPages"
                @change="onPageChange"
                align="right"
                  :limit="7"
                  :ellipsis-text="'...'"
                  :first-text="'«'"
                  :last-text="'»'"
                  :next-text="'›'"
                  :prev-text="'‹'"
                />
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Report Download Button -->
      <b-row class="mb-3">
        <b-col cols="12">
          <div class="d-flex justify-content-end">
            <b-button 
              variant="success" 
              size="sm"
              @click="downloadExcelReport"
              :disabled="loading"
            >
              <feather-icon icon="DownloadIcon" size="16" class="mr-2" />
              Download Excel Report
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Latest KPMG RPO Updates and Offer Process Stage Analysis -->
      <b-row>
        <!-- Latest KPMG RPO Updates - 4 columns -->
        <b-col cols="4" class="mb-3">
          <b-card title="Latest KPMG RPO Updates" class="timeline-card">
            <app-timeline>
              <app-timeline-item
                v-for="update in latestUpdates"
                :key="update.id"
                :title="update.title"
                :subtitle="update.description"
                :icon="update.icon"
                :time="update.timeAgo"
                :variant="update.variant"
              >
                <template #default>
                  <div v-if="update.details" class="timeline-details">
                    <div class="timeline-main-info">
                      <div class="candidate-name">{{ getCandidateName(update.candidate) }}</div>
                      <div class="position-name">{{ getPositionName(update.candidate) }}</div>
                    </div>
                    <div class="timeline-side-info">
                      <div v-if="update.status" class="status-info">
                        <b-badge :variant="getStatusVariant(update.status)">
                          {{ update.status }}
                        </b-badge>
                      </div>
                      <div v-if="update.recruiter" class="recruiter-info">
                        <i class="fas fa-user-tie mr-1"></i>
                        <span>{{ update.recruiter }}</span>
                      </div>
                      <div class="update-time">
                        <small class="text-muted">{{ update.timeAgo }}</small>
                      </div>
                    </div>
                  </div>
                </template>
              </app-timeline-item>
            </app-timeline>
          </b-card>
        </b-col>

        <!-- Offer Process Stage Analysis - 8 columns -->
        <b-col cols="8" class="mb-3">
          <b-card class="analysis-card">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5 class="card-title mb-0">Offer Process Stage Analysis</h5>
              <div class="unassigned-badge">
                <b-badge variant="warning" class="unassigned-count-badge">
                  <feather-icon icon="UserXIcon" size="14" class="mr-1" />
                  {{ getUnassignedCandidates() }} unassigned candidates
                </b-badge>
              </div>
            </div>
            <b-row>
              <!-- Stage-wise Candidate Count -->
              <b-col cols="6">
                <div class="analysis-section">
                  <h6 class="section-title">
                    <feather-icon icon="BarChart3Icon" size="16" class="mr-2" />
                    Stage-wise Candidate Count
                  </h6>
                  <div class="stage-counts">
                    <div v-for="stage in stageWiseCounts" :key="stage.name" class="stage-count-item">
                      <div class="stage-info">
                        <span class="stage-name">{{ stage.name }}</span>
                        <span class="stage-count">{{ stage.count }}</span>
                      </div>
                      <div class="progress-container">
                        <b-progress 
                          :value="stage.percentage" 
                          :variant="stage.variant"
                          height="8px"
                          class="stage-progress"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </b-col>

              <!-- POC Assignment Summary Table -->
              <b-col cols="6">
                <div class="analysis-section">
                  <h6 class="section-title">
                    <feather-icon icon="UsersIcon" size="16" class="mr-2" />
                    POC Assignment Summary
                  </h6>
                  <div class="poc-summary-table-container">
                    <div class="table-responsive">
                      <table class="table table-sm poc-summary-table">
                        <thead>
                          <tr>
                            <th class="poc-name-header">POC Name</th>
                            <th class="assigned-header">Assigned</th>
                            <th class="inprogress-header">In Progress</th>
                            <th class="completed-header">Completed</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="poc in pocSummaryData" :key="poc.name" class="poc-summary-row">
                            <td class="poc-name-cell">
                              <span class="poc-name">{{ poc.name }}</span>
                            </td>
                            <td class="assigned-cell">
                              <span class="count">{{ poc.totalCandidates }}</span>
                            </td>
                            <td class="inprogress-cell">
                              <span class="count">{{ poc.activeCandidates }}</span>
                            </td>
                            <td class="completed-cell">
                              <span class="count">{{ poc.totalCandidates - poc.activeCandidates }}</span>
                            </td>
                          </tr>
                          <!-- Total Row -->
                          <tr class="total-row">
                            <td class="poc-name-cell">
                              <strong>Total</strong>
                            </td>
                            <td class="assigned-cell">
                              <strong>{{ getTotalAssignedCandidates() }}</strong>
                            </td>
                            <td class="inprogress-cell">
                              <strong>{{ getTotalActiveCandidates() }}</strong>
                            </td>
                            <td class="completed-cell">
                              <strong>{{ getTotalAssignedCandidates() - getTotalActiveCandidates() }}</strong>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                
                <!-- Business Wise Count -->
                <div class="analysis-section mt-3">
                  <h6 class="section-title">
                    <feather-icon icon="BriefcaseIcon" size="16" class="mr-2" />
                    Business Wise Count
                  </h6>
                  <div class="business-summary-table-container">
                    <div class="table-responsive">
                      <table class="table table-sm business-summary-table">
                        <thead>
                          <tr>
                            <th class="business-name-header">Business</th>
                            <th class="assigned-header">Assigned</th>
                            <th class="inprogress-header">In Progress</th>
                            <th class="completed-header">Completed</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="business in businessSummaryData" :key="business.name" class="business-summary-row">
                            <td class="business-name-cell">
                              <span class="business-name">{{ business.name }}</span>
                            </td>
                            <td class="assigned-cell">
                              <span class="count">{{ business.assigned }}</span>
                            </td>
                            <td class="inprogress-cell">
                              <span class="count">{{ business.inProgress }}</span>
                            </td>
                            <td class="completed-cell">
                              <span class="count">{{ business.completed }}</span>
                            </td>
                          </tr>
                          <!-- Total Row -->
                          <tr class="total-row">
                            <td class="business-name-cell">
                              <strong>Total</strong>
                            </td>
                            <td class="assigned-cell">
                              <strong>{{ getTotalBusinessAssigned() }}</strong>
                            </td>
                            <td class="inprogress-cell">
                              <strong>{{ getTotalBusinessInProgress() }}</strong>
                            </td>
                            <td class="completed-cell">
                              <strong>{{ getTotalBusinessCompleted() }}</strong>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>

            <!-- POC-wise Allocation Table -->
            <div class="analysis-section mt-4">
              <h6 class="section-title">
                <feather-icon icon="BarChart2Icon" size="16" class="mr-2" />
                POC-wise Allocation (Detailed)
              </h6>
              <div class="poc-allocation-table-container">
                <div class="table-responsive">
                  <table class="table table-sm poc-allocation-table">
                    <thead>
                      <tr>
                        <th class="poc-name-header">POC Name</th>
                        <th v-for="stage in offerProcessStages" :key="stage.key" class="stage-header">
                          {{ stage.name }}
                        </th>
                        <th class="total-header">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="poc in pocAllocationTableData" :key="poc.name" class="poc-row">
                        <td class="poc-name-cell">
                          <div class="poc-info">
                            <span class="poc-name">{{ poc.name }}</span>
                          </div>
                        </td>
                        <td v-for="stage in offerProcessStages" :key="stage.key" class="stage-cell">
                          <div 
                            v-if="poc.stages[stage.key]"
                            class="stage-count"
                            v-b-tooltip.hover 
                            :title="getStageTooltip(poc, stage.key)"
                          >
                            <span class="count-number">{{ poc.stages[stage.key].count }}</span>
                            <small v-if="poc.stages[stage.key].comment" class="comment-indicator">
                              <feather-icon icon="MessageSquareIcon" size="10" />
                            </small>
                          </div>
                          <span v-else class="stage-count zero-count">0</span>
                        </td>
                        <td class="total-cell">
                          <strong>{{ poc.totalCandidates }}</strong>
                        </td>
                      </tr>
                      <!-- Total Row -->
                      <tr class="total-row">
                        <td class="poc-name-cell">
                          <strong>Total</strong>
                        </td>
                        <td v-for="stage in offerProcessStages" :key="stage.key" class="stage-cell">
                          <strong>{{ getStageTotal(stage.key) }}</strong>
                        </td>
                        <td class="total-cell">
                          <strong>{{ getGrandTotal() }}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Business and POC Assignment Modal -->
    <b-modal
      id="assignment-modal"
      title="Assign Business and POC"
      size="sm"
      :hide-footer="true"
      @hidden="resetAssignmentForm"
      class="assignment-modal"
    >
      <div v-if="selectedCandidate" class="assignment-content">
        <!-- Candidate Info -->
        <div class="candidate-info mb-3">
          <span class="candidate-name">{{ getCandidateName(selectedCandidate) }} - </span>
          <span class="candidate-position">{{ getPositionName(selectedCandidate) }}</span>
        </div>

        <!-- Assignment Form -->
        <b-form class="assignment-form">
          <b-form-group label="Business" label-for="business" class="mb-2">
            <b-form-select
              id="business"
              v-model="assignmentForm.business"
              :options="businessOptions"
              value-field="id"
              text-field="title"
              placeholder="Select Business"
              @change="onBusinessChange"
              size="sm"
            />
          </b-form-group>

          <b-form-group 
            v-if="assignmentForm.business && filteredSubBusinessOptions.length > 0"
            label="Sub Business" 
            label-for="subBusiness"
            class="mb-2"
          >
            <b-form-select
              id="subBusiness"
              v-model="assignmentForm.subBusiness"
              :options="filteredSubBusinessOptions"
              multiple
              value-field="id"
              text-field="title"
              placeholder="Select Sub Business"
              size="sm"
            />
          </b-form-group>

          <b-form-group label="POC" label-for="poc" class="mb-3">
            <b-form-select
              id="poc"
              v-model="assignmentForm.poc"
              :options="pocOptions"
              value-field="id"
              text-field="title"
              placeholder="Select POC"
              size="sm"
            />
          </b-form-group>

          <!-- Action Buttons -->
          <div class="modal-actions">
            <b-button variant="outline-secondary" @click="$bvModal.hide('assignment-modal')" size="sm" class="mr-2">
              Cancel
            </b-button>
            <b-button 
              variant="primary" 
              @click="submitAssignment"
              :disabled="!assignmentForm.business && !assignmentForm.poc"
              size="sm"
            >
              <feather-icon icon="CheckIcon" size="16" class="mr-1" />
              Assign
            </b-button>
          </div>
        </b-form>
      </div>
    </b-modal>

    <!-- Bulk Assignment Modal -->
    <b-modal
      id="bulk-assignment-modal"
      title="Bulk Assignment"
      size="sm"
      :hide-footer="true"
      @hidden="resetBulkAssignmentForm"
      class="bulk-assignment-modal"
    >
      <div class="bulk-assignment-content">
        <!-- Selection Summary -->
        <div class="selection-summary mb-3">
          <feather-icon icon="UsersIcon" size="16" class="mr-2" />
          <span>{{ selectedCandidates.length }} candidates selected</span>
        </div>
        
        <!-- Selected Candidates List -->
        <div class="selected-candidates-list mb-3">
          <div v-for="candidate in selectedCandidates.slice(0, 5)" :key="candidate.cid" class="candidate-item">
            <span class="candidate-name">{{ getCandidateName(candidate) }} - </span>
            <span class="candidate-position">{{ getPositionName(candidate) }}</span>
          </div>
          <div v-if="selectedCandidates.length > 5" class="more-candidates">
            <span class="text-muted">+{{ selectedCandidates.length - 5 }} more candidates</span>
          </div>
        </div>

        <!-- Assignment Form -->
        <b-form class="bulk-assignment-form">
          <b-form-group label="Business" label-for="bulk-business" class="mb-2">
            <b-form-select
              id="bulk-business"
              v-model="bulkAssignmentForm.business"
              :options="businessOptions"
              value-field="id"
              text-field="title"
              placeholder="Select Business"
              @change="onBulkBusinessChange"
              size="sm"
            />
          </b-form-group>

          <b-form-group 
            v-if="bulkAssignmentForm.business && filteredBulkSubBusinessOptions.length > 0"
            label="Sub Business" 
            label-for="bulk-subBusiness"
            class="mb-2"
          >
            <b-form-select
              id="bulk-subBusiness"
              v-model="bulkAssignmentForm.subBusiness"
              :options="filteredBulkSubBusinessOptions"
              multiple
              value-field="id"
              text-field="title"
              placeholder="Select Sub Business"
              size="sm"
            />
          </b-form-group>

          <b-form-group label="POC" label-for="bulk-poc" class="mb-3">
            <b-form-select
              id="bulk-poc"
              v-model="bulkAssignmentForm.poc"
              :options="pocOptions"
              value-field="id"
              text-field="title"
              placeholder="Select POC"
              size="sm"
            />
          </b-form-group>

          <!-- Action Buttons -->
          <div class="modal-actions">
            <b-button variant="outline-secondary" @click="$bvModal.hide('bulk-assignment-modal')" size="sm" class="mr-2">
              Cancel
            </b-button>
            <b-button 
              variant="primary" 
              @click="submitBulkAssignment"
              :disabled="!bulkAssignmentForm.business && !bulkAssignmentForm.poc"
              size="sm"
            >
              <feather-icon icon="CheckIcon" size="16" class="mr-1" />
              Assign to All Selected
            </b-button>
          </div>
        </b-form>
      </div>
    </b-modal>
    </div>
  </div>
</template>

<script>
import { BCard, BRow, BCol, BButton, BAvatar, BSpinner, BTable, BPagination, BFormGroup, BFormSelect, BFormInput, BBadge, BModal, BToast, BDropdown, BDropdownItem, BFormTextarea, BFormCheckbox, BProgress } from 'bootstrap-vue'
import vSelect from 'vue-select'
import candidateService from '@/services/candidate-service'
import adminConfigService from '@/services/admin-config-service'
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import { VBTooltip } from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import * as XLSX from 'xlsx'

export default {
  name: 'KPMGRPODashboard',
  data() {
    return {
      loading: true,
      candidates: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 25,
      
      // Summary counts
      totalCandidates: 0,
      shortlistedCount: 0,
      offeredCount: 0,
      joinedCount: 0,
      
      // All candidates data for KPI calculations
      allCandidates: [],
      
      // Pagination information from API
      paginationInfo: {
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        limit: 25,
        hasNextPage: false,
        hasPreviousPage: false,
        nextPage: null,
        previousPage: null
      },
      
      // Latest updates timeline
      latestUpdates: [],
      
      // Filters
      filters: {
        status: ['Shortlisted', 'Offered', 'Offer Accepted'],
        business: '',
        poc: '',
        search: ''
      },
      
      // Business and POC options
      businessOptions: [],
      subBusinessOptions: [],
      pocOptions: [],
      
      // Assignment modals
      showBusinessModal: false,
      showPOCModal: false,
      selectedCandidate: null,
      assignmentForm: {
        business: null,
        subBusiness: [],
        poc: null
      },
      bulkAssignmentForm: {
        business: null,
        subBusiness: [],
        poc: null
      },
      
      // Bulk selection
      selectedCandidates: [],
      tableKey: 0,
      selectAll: false,
      
      // Options for filters
      statusOptions: [
        { value: '', text: 'All Status' },
        { value: 'Shortlisted', text: 'Shortlisted' },
        { value: 'Offer Accepted', text: 'Offer Accepted' },
        { value: 'Offered', text: 'Offered' },
        { value: 'Joined', text: 'Joined' },
        { value: 'NotOffered', text: 'Not Offered' },
        { value: 'OfferDrop', text: 'Offer Drop' }
      ],
      businessFilterOptions: [
        { value: '', text: 'All Business' },
        { value: 'null', text: 'Unassigned Business' }
      ],
      pocFilterOptions: [
        { value: '', text: 'All POC' },
        { value: 'null', text: 'Unassigned POC' }
      ],
      
      // Dummy data for Offer Process Stage Analysis
      dummyStageWiseCounts: [
        { name: 'Tagging', count: 45, percentage: 45, variant: 'warning' },
        { name: 'Job Application', count: 38, percentage: 38, variant: 'info' },
        { name: 'CAF', count: 32, percentage: 32, variant: 'primary' },
        { name: 'Documentation', count: 28, percentage: 28, variant: 'secondary' },
        { name: 'TAF', count: 25, percentage: 25, variant: 'dark' },
        { name: 'HAF', count: 22, percentage: 22, variant: 'light' },
        { name: 'HR Discussion', count: 18, percentage: 18, variant: 'success' },
        { name: 'Fitment', count: 15, percentage: 15, variant: 'danger' },
        { name: 'OfferDesk', count: 12, percentage: 12, variant: 'warning' },
        { name: 'KGS BGV', count: 8, percentage: 8, variant: 'info' }
      ],
      
      // Dummy data for Business Summary
      dummyBusinessSummaryData: [
        { name: 'KYC F&A', assigned: 25, inProgress: 18, completed: 7 },
        { name: 'MFT', assigned: 15, inProgress: 12, completed: 3 },
        { name: 'CH-Non Tech', assigned: 8, inProgress: 6, completed: 2 },
        { name: 'GENAI(P)', assigned: 12, inProgress: 10, completed: 2 },
        { name: 'GENAI(M)', assigned: 6, inProgress: 4, completed: 2 }
      ],
      
            dummyPocAllocationData: [
        {
          name: 'Suyash Shinde',
          role: 'SECONDARY',
          totalCandidates: 5,
          stages: {
            tagging: { count: 5, status: 'Tagged', comment: 'All candidates tagged with ReqId assigned' },
            jobApplication: { count: 0, status: 'Pending', comment: null },
            caf: { count: 0, status: 'Pending', comment: null },
            documentation: { count: 0, status: 'Pending', comment: null },
            haf: { count: 0, status: 'Pending', comment: null },
            taf: { count: 0, status: 'Pending', comment: null },
            hrDiscussion: { count: 0, status: 'Pending', comment: null },
            fitment: { count: 0, status: 'Pending', comment: null },
            offerDesk: { count: 0, status: 'Pending', comment: null },
            kgsBGV: { count: 0, status: 'Pending', comment: null },
            clientBGV: { count: 0, status: 'Pending', comment: null }
          }
        },
        {
          name: 'Priyanka Gaikwad',
          role: 'PRIMARY',
          totalCandidates: 9,
          stages: {
            tagging: { count: 0, status: 'Completed', comment: null },
            jobApplication: { count: 0, status: 'Completed', comment: null },
            caf: { count: 0, status: 'Completed', comment: null },
            documentation: { count: 0, status: 'Completed', comment: null },
            haf: { count: 9, status: 'In Process', comment: 'HAF forms being reviewed by HR team' },
            taf: { count: 0, status: 'Pending', comment: null },
            hrDiscussion: { count: 0, status: 'Pending', comment: null },
            fitment: { count: 0, status: 'Pending', comment: null },
            offerDesk: { count: 0, status: 'Pending', comment: null },
            kgsBGV: { count: 0, status: 'Pending', comment: null },
            clientBGV: { count: 0, status: 'Pending', comment: null }
          }
        },
        {
          name: 'Tanisha P',
          role: 'SECONDARY',
          totalCandidates: 0,
          stages: {
            tagging: { count: 0, status: 'Pending', comment: null },
            jobApplication: { count: 0, status: 'Pending', comment: null },
            caf: { count: 0, status: 'Pending', comment: null },
            documentation: { count: 0, status: 'Pending', comment: null },
            haf: { count: 0, status: 'Pending', comment: null },
            taf: { count: 0, status: 'Pending', comment: null },
            hrDiscussion: { count: 0, status: 'Pending', comment: null },
            fitment: { count: 0, status: 'Pending', comment: null },
            offerDesk: { count: 0, status: 'Pending', comment: null },
            kgsBGV: { count: 0, status: 'Pending', comment: null },
            clientBGV: { count: 0, status: 'Pending', comment: null }
          }
        }
      ],
      
      // Table fields
       tableFields: [
         { key: 'selection', label: '', thStyle: { width: '50px' }, visible: false },
         { key: 'candidate_name', label: 'Candidate', sortable: true, thStyle: { width: '150px' } },
         { key: 'candidateStatus', label: 'Status', sortable: true, thStyle: { width: '120px' } },
         { key: 'position_name', label: 'Position', sortable: true, thStyle: { width: '200px' } },
         { key: 'assignedTo', label: 'Assigned To', sortable: true, thStyle: { width: '150px' } },
         { key: 'offerProcessStage', label: 'Offer Process Stage', sortable: true, thStyle: { width: '180px' } },
         { key: 'actions', label: 'Actions', thStyle: { width: '120px' } }
       ]
    }
  },
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BAvatar,
    BSpinner,
    BTable,
    BPagination,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BBadge,
    BDropdown,
    BDropdownItem,
    AppTimeline,
    AppTimelineItem,
    AppCollapse,
    AppCollapseItem,
    VBTooltip,
    BFormCheckbox,
    BProgress,
    BFormTextarea,
    vSelect
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  async mounted() {
    // Check access level f
    await this.loadBusinessAndPOCOptions()
    if (!this.hasAccess) {
      return // Don't load data if user doesn't have access
    }
    
    
    await this.loadCandidates()
    // await this.loadBusinessAndPOCOptions()
    
    // Show access level notification
    // this.showAccessLevelNotification()
    
    // Debug POC assignment (remove in production)
    this.debugPOCAssignment()
  },
  
  computed: {
    // Access control computed property
    hasAccess() {
      
      // Check if user has manage kpmg_rpo_dashboard permission
      if (this.$can('manage', 'kpmg_rpo_dashboard')) {
        return true
      }
      
      // Check if user has read kpmg_rpo_dashboard permission and is a POC
      if (this.$can('read', 'kpmg_rpo_dashboard')) {
        // Check if user is a POC (has POC role or is in POC list)
        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        const userPOC = this.pocOptions.find(poc => 
          poc.title.includes(userData.emp_id)
        )
        console.log('userPOC', userPOC, userData.emp_id, this.pocOptions)
        return userPOC !== undefined
      }
      
      return false
    },
    
    filteredSubBusinessOptions() {
      if (!this.assignmentForm.business) {
        return []
      }
      const selectedBusinessId = String(this.assignmentForm.business)
      
      return this.subBusinessOptions.filter(subBusiness => {
        const matches = String(subBusiness.abbrev) === selectedBusinessId
        return matches
      })
    },
    filteredBulkSubBusinessOptions() {
      if (!this.bulkAssignmentForm.business) {
        return []
      }
      const selectedBusinessId = String(this.bulkAssignmentForm.business)
      return this.subBusinessOptions.filter(subBusiness =>
        String(subBusiness.abbrev) === selectedBusinessId
      )
    },
    isAllSelected() {
      return this.candidates.length > 0 && this.selectedCandidates.length === this.candidates.length
    },
    isIndeterminate() {
      return this.selectedCandidates.length > 0 && this.selectedCandidates.length < this.candidates.length
    },
    // Role-based access control computed properties
    currentUserData() {
      return JSON.parse(localStorage.getItem('userData') || '{}')
    },
    currentUserEmpId() {
      return this.currentUserData.emp_id || ''
    },
    canAssignBusinessAndPOC() {
      // Check if user has 'manage' 'post_selection_process' permission
      if (this.$can('manage', 'post_selection_process')) {
        return true
      }
      
      // Check if current user is a PRIMARY or BKP POC
      const userPOC = this.pocOptions.find(poc => 
        poc.title.includes(this.currentUserEmpId)
      )
      
      return userPOC && (userPOC.abbrev === 'PRIMARY' || userPOC.abbrev === 'BKP')
    },
    assignedCandidates() {
      // Since API now handles pagination, just return the candidates from current page
      // Apply POC-based filtering only if needed
      if (!this.canAssignBusinessAndPOC) {
        // For SECONDARY POCs, show only candidates assigned to them
        const userPOC = this.pocOptions.find(poc => 
          poc.title.includes(this.currentUserEmpId)
        )
        
        if (userPOC && userPOC.abbrev === 'SECONDARY') {
          return this.candidates.filter(candidate => {
            // Only check extra_details.poc if extra_details exists (KPMG RPO candidates only)
            if (!candidate.extra_details) {
              return false // Non-KPMG candidates are not assigned to any POC
            }
            
            let extraDetails = candidate.extra_details
            if (typeof extraDetails === 'string') {
              try {
                extraDetails = JSON.parse(extraDetails)
              } catch (error) {
                console.error('Error parsing extra_details:', error)
                return false
              }
            }
            const assignedPOC = extraDetails && extraDetails.poc ? extraDetails.poc : null
            
            // Check if candidate is assigned to current user
            return assignedPOC === userPOC.title
          })
        } else {
          // For users who are not PRIMARY/BKP POCs and don't have permission, show no candidates
          return []
        }
      }
      
      // For users with full access, return all candidates from current page
      return this.candidates
    },
    filteredTableFields() {
      // Filter table fields based on user permissions
      return this.tableFields.filter(field => {
        if (field.key === 'selection') {
          return this.canAssignBusinessAndPOC
        }
        return true
      })
    },
    
    // Computed property for total rows after POC filtering
    totalFilteredRows() {
      // Since API now handles pagination, use totalRecords from pagination info
      // For SECONDARY POCs, we need to calculate the actual count they can see
      if (!this.canAssignBusinessAndPOC) {
        const userPOC = this.pocOptions.find(poc => 
          poc.title.includes(this.currentUserEmpId)
        )
        
        if (userPOC && userPOC.abbrev === 'SECONDARY') {
          // For SECONDARY POCs, count only candidates assigned to them
          return this.candidates.filter(candidate => {
            if (!candidate.extra_details) return false
            
            let extraDetails = candidate.extra_details
            if (typeof extraDetails === 'string') {
              try {
                extraDetails = JSON.parse(extraDetails)
              } catch (error) {
                return false
              }
            }
            const assignedPOC = extraDetails && extraDetails.poc ? extraDetails.poc : null
            return assignedPOC === userPOC.title
          }).length
        } else {
          return 0
        }
      }
      
      // For users with full access, return total records from pagination info
      console.log('totalFilteredRows - paginationInfo:', this.paginationInfo)
      return this.paginationInfo.totalRecords || 0
    },
    
    // Computed properties for Offer Process Stage Analysis
    stageWiseCounts() {
      return this.calculateStageWiseCounts()
    },
    
    pocAllocationData() {
      return this.calculatePOCAllocationData()
    },
    
    // Offer process stages for table headers
    offerProcessStages() {
      return [
        { key: 'tagging', name: 'Tagging' },
        { key: 'jobApplication', name: 'Job Application' },
        { key: 'caf', name: 'CAF' },
        { key: 'documentation', name: 'Documentation' },
        { key: 'haf', name: 'HAF' },
        { key: 'taf', name: 'TAF' },
        { key: 'hrDiscussion', name: 'HR Discussion' },
        { key: 'fitment', name: 'Fitment' },
        { key: 'offerDesk', name: 'OfferDesk' },
        { key: 'kgsBGV', name: 'KGS BGV' },
        { key: 'clientBGV', name: 'Client BGV' }
      ]
    },
    
    // Table format data for POC allocation
    pocAllocationTableData() {
      return this.calculatePOCAllocationData()
    },
    
    // POC Summary data for the 4-column section
    pocSummaryData() {
      const pocAllocationData = this.calculatePOCAllocationData()
      const totalCandidates = this.getTotalAssignedCandidates()
      
      return pocAllocationData.map(poc => {
        const percentage = totalCandidates > 0 ? Math.round((poc.totalCandidates / totalCandidates) * 100) : 0
        const activeCandidates = this.calculateActiveCandidates(poc)
        
        return {
          name: poc.name,
          role: poc.role,
          totalCandidates: poc.totalCandidates,
          activeCandidates: activeCandidates,
          percentage: percentage,
          variant: this.getPOCVariant(poc.role)
        }
      })
    },
    // Business Summary data
    businessSummaryData() {
      return this.calculateBusinessSummaryData()
    }
  },
  
  methods: {
    async loadCandidates() {
      try {
        this.loading = true
        
        // Get user data from localStorage
        const userData = JSON.parse(localStorage.getItem('userData'))
        const userRole = userData.roles[0]
        const emp_id = userData.emp_id
        const userPOC = userData.emp_id + ' - ' + userData.first_name + ' ' + userData.last_name

        const isPOC = this.pocOptions.some(poc => 
          poc.title.includes(userPOC)
        )
        console.log('isPOC', isPOC, userPOC)
        
        // Prepare parameters for KPMG RPO candidates API
        let statusParam = null
        if (this.filters.status && this.filters.status.length > 0) {
          // If "All Status" is selected (empty value), get all status values
          if (this.filters.status.includes('')) {
            statusParam = this.statusOptions
              .filter(option => option.value !== '') // Exclude "All Status" option
              .map(option => option.value)
          } else {
            statusParam = this.filters.status
          }
        }
        
        const params = {
          page: this.currentPage,
          limit: this.perPage,
          business: this.filters.business || null,
          subBusiness: this.filters.subBusiness || null,
          status: statusParam,
          poc: this.filters.poc || null,
          emp_id: isPOC ? null : emp_id,
          filter: this.filters.search || null
        }
        
        // Debug POC filter
        console.log('POC Filter Debug:', {
          selectedPOC: this.filters.poc,
          pocFilterOptions: this.pocFilterOptions,
          pocOptions: this.pocOptions,
          finalPOCParam: params.poc
        })
        
        // POC Filter Logic: Compare full POC title (emp_id + name) from extra_details.poc
        // Only filter by "Unassigned POC" for candidates where poc is not assigned or not present
        if (this.filters.poc && this.filters.poc !== '') {
          if (this.filters.poc === 'Unassigned POC') {
            // For "Unassigned POC", we want candidates where poc is null, empty, or not present
            params.poc = 'null'
          } else {
            // For specific POC, use the full title as-is (emp_id + name)
            params.poc = this.filters.poc
          }
        }
        
        console.log('API Parameters:', params)
        
        // Load KPMG RPO candidates data
        const response = await candidateService.getKPMGRPOCandidates(params)
        
        if (response.status === "OK") {
          const candidates = response.data.candidates || []
          const statusWiseCount = response.data.statusWise || []
          const latestRecords = response.data.latestRecords || []
          const pagination = response.data.pagination || {}
          
          console.log('KPMG RPO candidates data received:', {
            candidatesReceived: candidates.length,
            statusWiseCount: statusWiseCount.length,
            latestRecords: latestRecords.length,
            pagination: pagination,
            filters: {
              business: this.filters.business,
              status: this.filters.status,
              search: this.filters.search
            }
          })
          
          // Store candidates for current page
          this.candidates = candidates.map(candidate => ({
            ...candidate,
            selected: false
          }))
          
          // Store pagination information
          this.paginationInfo = {
            currentPage: pagination.currentPage || 1,
            totalPages: pagination.totalPages || 1,
            totalRecords: pagination.totalRecords || 0,
            limit: pagination.limit || this.perPage,
            hasNextPage: pagination.hasNextPage || false,
            hasPreviousPage: pagination.hasPreviousPage || false,
            nextPage: pagination.nextPage || null,
            previousPage: pagination.previousPage || null
          }
          
          // Update current page to match API response
          this.currentPage = this.paginationInfo.currentPage
          
          // Store total count for pagination
          this.totalRows = this.paginationInfo.totalRecords
          
          // Use statusWiseCount for KPI calculations
          this.calculateSummaryCountsFromAPI(statusWiseCount)
          // Use latestRecords for timeline
          this.generateLatestUpdatesFromAPI(latestRecords)
        } else {
          console.error('API error:', response)
          this.$bvToast.toast('Error loading KPMG RPO candidates', {
            title: 'Error',
            variant: 'danger',
            solid: true
          })
        }
        
      } catch (error) {
        console.error('Error loading candidates:', error)
        this.$bvToast.toast('Failed to load candidates', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.loading = false
      }
    },
    
    // calculateSummaryCounts() {
    //   // Calculate counts from all candidates data, not just current page
    //   this.totalCandidates = this.allCandidates.length
    //   this.shortlistedCount = this.allCandidates.filter(c => c.candidateStatus === 'Shortlisted').length
    //   this.offeredCount = this.allCandidates.filter(c => c.candidateStatus === 'Offered').length
    //   this.joinedCount = this.allCandidates.filter(c => c.candidateStatus === 'Joined').length
    // },
    
    calculateSummaryCountsFromAPI(statusWiseCount) {
      // Calculate counts from API statusWiseCount data
      this.totalCandidates = statusWiseCount.reduce((total, item) => total + parseInt(item.count), 0)
      this.shortlistedCount = statusWiseCount.find(item => item.candidateStatus === 'Shortlisted')?.count || 0
      this.offeredCount = statusWiseCount.find(item => item.candidateStatus === 'Offered')?.count || 0
      this.joinedCount = statusWiseCount.find(item => item.candidateStatus === 'Joined')?.count || 0
      console.log('totalCandidates', statusWiseCount, this.totalCandidates, this.shortlistedCount, this.offeredCount, this.joinedCount)
    },
     
    generateLatestUpdates() {
      const updates = []
      let updateId = 1
      
      // Sort candidates by updated_at date (most recent first)
      const sortedCandidates = [...this.allCandidates].sort((a, b) => 
        new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
      )
      
      // Take the latest 10 candidates and create timeline updates
      sortedCandidates.slice(0, 10).forEach(candidate => {
        const updateDate = new Date(candidate.updated_at || candidate.created_at)
        const timeAgo = this.getTimeAgo(updateDate)
        
        let updateType, title, description, icon, variant
        
        const candidateName = candidate.candidate_name || candidate.candidateName
        
        switch (candidate.candidateStatus) {
          case 'Joined':
            // Final status: Candidate is Shortlisted, Offered and Joined
            updateType = 'joined'
            title = 'Candidate Successfully Joined'
            description = `${candidateName} has completed the full process and joined for ${candidate.position_name}`
            icon = 'UserCheckIcon'
            variant = 'success'
            break
          case 'Offered':
            // Shortlisted and then Offered (Joined is still pending)
            updateType = 'offered'
            title = 'Offer Extended - Awaiting Response'
            description = `${candidateName} has been offered ${candidate.position_name} position`
            icon = 'HandshakeIcon'
            variant = 'info'
            break
          case 'Shortlisted':
            // Offered and Joined is still pending
            updateType = 'shortlisted'
            title = 'Candidate Shortlisted - In Process'
            description = `${candidateName} has been shortlisted for ${candidate.position_name}`
            icon = 'UserIcon'
            variant = 'warning'
            break
          case 'NotOffered':
            // Was Shortlisted but not offered
            updateType = 'not-offered'
            title = 'Candidate Not Offered'
            description = `${candidateName} was shortlisted but not offered ${candidate.position_name}`
            icon = 'XCircleIcon'
            variant = 'danger'
            break
          case 'OfferDrop':
            // Was Shortlisted, Offered but candidate dropped the offer
            updateType = 'offer-drop'
            title = 'Candidate Dropped Offer'
            description = `${candidateName} was offered but declined ${candidate.position_name}`
            icon = 'UserMinusIcon'
            variant = 'secondary'
            break
          default:
            updateType = 'added'
            title = 'New Candidate Added'
            description = `${candidateName} has been added to the pipeline for ${candidate.position_name}`
            icon = 'UserPlusIcon'
            variant = 'primary'
        }
        
        updates.push({
          id: updateId++,
          type: updateType,
          title,
          description,
          timeAgo,
          candidate: {
            candidate_name: candidate.candidate_name || candidate.candidateName,
            position_name: candidate.position_name,
            profileImage: candidate.profileImage
          },
          status: candidate.candidateStatus,
          recruiter: candidate.recruiter,
          details: true,
          icon: icon,
          variant: variant
        })
      })
      
      this.latestUpdates = updates
    },
    
    generateLatestUpdatesFromAPI(latestRecords) {
      const updates = []
      let updateId = 1
      
      // Process latestRecords from API
      latestRecords.forEach(record => {
        const updateDate = new Date(record.updatedAt || record.createdAt)
        const timeAgo = this.getTimeAgo(updateDate)
        
        let updateType, title, description, icon, variant
        
        const candidateName = record.candidate_name || record.candidateName
        
        switch (record.candidateStatus) {
          case 'Joined':
            // Final status: Candidate is Shortlisted, Offered and Joined
            updateType = 'joined'
            title = 'Candidate Successfully Joined'
            description = `${candidateName} has completed the full process and joined for ${record.position_name}`
            icon = 'UserCheckIcon'
            variant = 'success'
            break
          case 'Offered':
            // Shortlisted and then Offered (Joined is still pending)
            updateType = 'offered'
            title = 'Offer Extended - Awaiting Response'
            description = `${candidateName} has been offered ${record.position_name} position`
            icon = 'HandshakeIcon'
            variant = 'info'
            break
          case 'Shortlisted':
            // Offered and Joined is still pending
            updateType = 'shortlisted'
            title = 'Candidate Shortlisted - In Process'
            description = `${candidateName} has been shortlisted for ${record.position_name}`
            icon = 'UserIcon'
            variant = 'warning'
            break
          case 'NotOffered':
            // Was Shortlisted but not offered
            updateType = 'not-offered'
            title = 'Candidate Not Offered'
            description = `${candidateName} was shortlisted but not offered ${record.position_name}`
            icon = 'XCircleIcon'
            variant = 'danger'
            break
          case 'OfferDrop':
            // Was Shortlisted, Offered but candidate dropped the offer
            updateType = 'offer-drop'
            title = 'Candidate Dropped Offer'
            description = `${candidateName} was offered but declined ${record.position_name}`
            icon = 'UserMinusIcon'
            variant = 'secondary'
            break
          default:
            updateType = 'added'
            title = 'New Candidate Added'
            description = `${candidateName} has been added to the pipeline for ${record.position_name}`
            icon = 'UserPlusIcon'
            variant = 'primary'
        }
        
        updates.push({
          id: updateId++,
          type: updateType,
          title,
          description,
          timeAgo,
          candidate: {
            candidate_name: record.candidate_name || record.candidateName,
            position_name: record.position_name,
            profileImage: record.profileImage
          },
          status: record.candidateStatus,
          recruiter: record["Employee Name"] || record.recruiter,
          details: true,
          icon: icon,
          variant: variant
        })
      })
      
      this.latestUpdates = updates
    },
    

    
    onPageChange(page) {
      this.currentPage = page
      this.loadCandidates()
    },
     
    onPageSizeChange() {
      this.currentPage = 1
      this.perPage = this.paginationInfo.limit
      this.loadCandidates()
    },
    
    clearFilters() {
      this.filters = {
        status: ['Shortlisted', 'Offered', 'Offer Accepted'],
        business: '',
        poc: '',
        search: ''
      }
      this.currentPage = 1
      this.loadCandidates()
    },
    
    onSearchInput() {
      const searchLength = this.filters.search.length
      
      // Only trigger search if length is 4 or more, or if search is empty
      if (searchLength >= 4 || searchLength === 0) {
        this.currentPage = 1
        this.loadCandidates()
      }
    },
    
    clearSearch() {
      this.filters.search = ''
      this.currentPage = 1
      this.loadCandidates()
      
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Search cleared',
          text: 'Showing all candidates',
          icon: 'CheckCircleIcon',
          variant: 'success',
        },
      })
    },
    
    getStatusVariant(status) {
      // Extract status from offer process string (e.g., "Tagging: Pending" -> "Pending")
      let statusToCheck = status
      if (status.includes(': ')) {
        statusToCheck = status.split(': ')[1]
      }
      
      const variants = {
         'Shortlisted': 'warning',
         'Offer Accepted': 'info',
         'Offered': 'info',
         'Joined': 'success',
         'NotOffered': 'danger',
         'OfferDrop': 'secondary',
         'Pending': 'warning',
         'Completed': 'success',
         'Extended': 'info',
         'Triggered': 'primary',
         'Rejected': 'danger',
         'Raised': 'info',
         'InProcess': 'primary',
         'Requested': 'info',
         'Initiated': 'primary',
         'Cleared': 'success',
         'Failed': 'danger'
       }
       return variants[statusToCheck] || 'primary'
     },
     
    getStatusDotClass(status) {
      const dotClasses = {
         'Shortlisted': 'status-dot-warning',
         'Offered': 'status-dot-info',
         'Joined': 'status-dot-success',
         'NotOffered': 'status-dot-danger',
         'OfferDrop': 'status-dot-secondary'
       }
       return dotClasses[status] || 'status-dot-primary'
    },
     

     

     
    getTimeAgo(date) {
      const now = new Date()
      const diffInSeconds = Math.floor((now - date) / 1000)
      
      if (diffInSeconds < 60) {
        return `${diffInSeconds} seconds ago`
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60)
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
      } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600)
        return `${hours} hour${hours > 1 ? 's' : ''} ago`
      } else {
        const days = Math.floor(diffInSeconds / 86400)
        return `${days} day${days > 1 ? 's' : ''} ago`
      }
    },
  
        viewCandidate(candidate) {
      // Navigate to candidate view
      this.$router.push({
        name: 'candidates-view',
        params: { id: candidate.id },
        query: { client: 'KPMG RPO' }
      })
    },
    
    sendEmail(candidate) {
      // TODO: Implement email functionality
      this.$bvToast.toast(`Email sent to ${candidate.candidateName}`, {
        title: 'Success',
        variant: 'success',
        solid: true
      })
    },
    
    editCandidate(candidate) {
      // TODO: Implement edit functionality
      this.$bvToast.toast(`Edit ${candidate.candidateName}`, {
        title: 'Info',
        variant: 'info',
        solid: true
      })
    },
    
    viewHistory(candidate) {
      // TODO: Implement history view
      this.$bvToast.toast(`View history for ${candidate.candidateName}`, {
        title: 'Info',
        variant: 'info',
        solid: true
      })
    },
    
    toggleSelection(candidate) {
      const index = this.selectedCandidates.findIndex(selected => selected.cid === candidate.cid)
      if (index > -1) {
        this.selectedCandidates.splice(index, 1)
        candidate.selected = false
      } else {
        this.selectedCandidates.push(candidate)
        candidate.selected = true
      }
      this.updateSelectAllState()
    },
    
    toggleSelectAll() {
      if (this.selectAll) {
        // Select all
        this.candidates.forEach(candidate => {
          candidate.selected = true
          if (!this.selectedCandidates.some(selected => selected.cid === candidate.cid)) {
            this.selectedCandidates.push(candidate)
          }
        })
      } else {
        // Deselect all
        this.candidates.forEach(candidate => {
          candidate.selected = false
        })
        this.selectedCandidates = []
      }
    },
    
    updateSelectAllState() {
      this.selectAll = this.isAllSelected
    },
    
    onRowSelected(items) {
      this.selectedCandidates = items
    },
    
    clearSelection() {
      this.selectedCandidates = []
      this.selectAll = false
      
      // Clear selection state for all candidates
      this.candidates.forEach(candidate => {
        candidate.selected = false
      })
      
      // Force table to re-render
      this.tableKey++
    },
    

    

    
    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    
    getCandidateName(candidate) {
      const fullName = candidate.candidate_name || candidate.candidateName || ''
      // Remove candidate ID pattern (e.g., "SHC-123 - John Doe" -> "John Doe")
      return fullName.replace(/^SHC-\d+\s*-\s*/, '').trim()
    },
    
    getPositionName(candidate) {
      const positionName = candidate.position_name || ''
      // Remove job ID pattern (e.g., "JOB-456 - Software Engineer" -> "Software Engineer")
      return positionName.replace(/^[A-Z]+-\d+\s*-\s*/, '').trim()
    },
    
    // Load business and POC options
    // 
    // BUSINESS FILTERING LOGIC:
    // 1. Business filter shows only MAIN BUSINESS names (not sub-business names)
    // 2. Business data comes from candidate.extra_details.business or falls back to candidate.business
    // 3. If a business name is not in the main business list, it's categorized as "Unassigned"
    // 4. The filter options include: "All Business", "Unassigned Business", and all main business names
    // 5. API receives the business name as-is for filtering
    //
    // POC FILTERING LOGIC:
    // 1. POC filter shows full POC titles (e.g., "EMP123 - John Doe")
    // 2. POC data comes from candidate.extra_details.poc (full title with emp_id + name)
    // 3. The getAssignedPOCName() method extracts only the name part (e.g., "John Doe") for display
    // 4. For filtering, we compare the full POC title from extra_details.poc with the filter value
    // 5. "Unassigned POC" filter shows candidates where poc is null, empty, or not present in extra_details
    // 6. The filter options include: "All POC", "Unassigned POC", and all POC titles
    async loadBusinessAndPOCOptions() {
      try {
        const res = await adminConfigService.getClientDropdownList('KPMG RPO')
        console.log('KPMG RPO dropdown data loaded:', res.data.data)
        
        // Format business options for dropdown
        this.businessOptions = (res.data.data.businessList || []).map(business => ({
          id: business.id,
          title: business.title,
          abbrev: business.abbrev
        }))
        
        // Update business filter options with actual business names
        this.businessFilterOptions = [
          { value: '', text: 'All Business' },
          { value: 'null', text: 'Unassigned Business' },
          ...this.businessOptions.map(business => ({
            value: business.title,
            text: business.title
          }))
        ]
        
        // Process sub-business options to add unique keys and remove duplicates
        const rawSubBusinessOptions = res.data.data.subBusinessList || []
        const uniqueSubBusinesses = new Map()
        
        rawSubBusinessOptions.forEach((subBusiness, index) => {
          const key = `${subBusiness.abbrev}-${subBusiness.title}`
          if (!uniqueSubBusinesses.has(key)) {
            uniqueSubBusinesses.set(key, {
              id: subBusiness.id || index,
              title: subBusiness.title,
              abbrev: subBusiness.abbrev,
              key: key
            })
          }
        })
        
        this.subBusinessOptions = Array.from(uniqueSubBusinesses.values())
        
        // Format POC options for dropdown
        this.pocOptions = (res.data.data.pocList || []).map((poc, index) => ({
          id: poc.id || index,
          title: poc.title,
          name: poc.name,
          abbrev: poc.abbrev
        }))
        
        // Update POC filter options with actual POC names
        this.pocFilterOptions = [
          { value: '', text: 'All POC' },
          { value: 'Unassigned POC', text: 'Unassigned POC' },
          ...this.pocOptions.map(poc => ({
            value: poc.title,
            text: poc.title
          }))
        ]
        
      } catch (error) {
        console.error('Error loading KPMG RPO dropdown data:', error)
      }
    },
    
          // Combined Business and POC assignment methods
    assignBusinessAndPOC(candidate) {
      this.selectedCandidate = candidate
      this.resetAssignmentForm()
      this.$bvModal.show('assignment-modal')
    },
    
    onBusinessChange() {
      this.assignmentForm.subBusiness = []
    },
    
    onBulkBusinessChange() {
      this.bulkAssignmentForm.subBusiness = []
    },
    
    submitAssignment() {
      if (!this.assignmentForm.business && !this.assignmentForm.poc) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Please select at least one assignment (Business or POC)',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
        return
      }
      
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      const username = `${userData.emp_id} - ${userData.first_name} ${userData.last_name}`
      const user = `[{"title" : "${userData.first_name} ${userData.last_name}", "emp_id": "${userData.emp_id}"}]`;
      
      // Get existing extra_details or initialize empty object (only for KPMG RPO candidates)
      let existingExtraDetails = {}
      if (this.selectedCandidate.extra_details) {
        try {
          existingExtraDetails = typeof this.selectedCandidate.extra_details === 'string' 
            ? JSON.parse(this.selectedCandidate.extra_details) 
            : this.selectedCandidate.extra_details
        } catch (error) {
          console.error('Error parsing extra_details:', error)
          existingExtraDetails = {}
        }
      }
      
      // Prepare new assignment data
      const newAssignmentData = {
        assignedBy: username,
        assignedAt: new Date().toISOString()
      }
      
      if (this.assignmentForm.business) {
        const selectedBusiness = this.businessOptions.find(b => b.id === this.assignmentForm.business)
        newAssignmentData.business = selectedBusiness ? selectedBusiness.title : this.assignmentForm.business
        newAssignmentData.subBusiness = this.assignmentForm.subBusiness.map(id => {
          const subBusiness = this.subBusinessOptions.find(sb => sb.id === id)
          return subBusiness ? subBusiness.title : id
        })
      }
      
      if (this.assignmentForm.poc) {
        const selectedPOC = this.pocOptions.find(p => p.id === this.assignmentForm.poc)
        newAssignmentData.poc = selectedPOC ? selectedPOC.title : this.assignmentForm.poc
      }
      
      // Merge existing and new data (append, don't override)
      const mergedExtraDetails = {
        ...existingExtraDetails,
        ...newAssignmentData
      }
      
      const payload = {
        candidateStatus: this.selectedCandidate.candidateStatus,
        updatedBy: user,
        extra_details: mergedExtraDetails
      }
      
      candidateService.updateCandidateStatus(this.selectedCandidate.cid, this.selectedCandidate.jid, payload).then(
        response => {
          if (response.status === 'OK') {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Assignment completed successfully',
                icon: 'CheckCircleIcon',
                variant: 'success',
              },
            })
            
            // Refresh candidates data
            this.loadCandidates()
            
            // Close modal
            this.$bvModal.hide('assignment-modal')
            this.resetAssignmentForm()
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Failed to complete assignment',
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
            })
          }
        },
        error => {
          console.error('Error completing assignment:', error)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error completing assignment',
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          })
        }
      )
    },
    
          // Bulk assignment methods
    openBulkAssignmentModal() {
      if (this.selectedCandidates.length === 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Please select candidates first',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
        return
      }
      
      this.resetBulkAssignmentForm()
      this.$bvModal.show('bulk-assignment-modal')
    },
    
    submitBulkAssignment() {
      if (!this.bulkAssignmentForm.business && !this.bulkAssignmentForm.poc) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Please select at least one assignment',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
        return
      }
      
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      const user = `[{"title" : "${userData.first_name} ${userData.last_name}", "emp_id": "${userData.emp_id}"}]`
      
      let successCount = 0
      let errorCount = 0
      
      // Process each selected candidate
      const promises = this.selectedCandidates.map(candidate => {
        // Get existing extra_details or initialize empty object (only for KPMG RPO candidates)
        let existingExtraDetails = {}
        if (candidate.extra_details) {
          try {
            existingExtraDetails = typeof candidate.extra_details === 'string' 
              ? JSON.parse(candidate.extra_details) 
              : candidate.extra_details
          } catch (error) {
            console.error('Error parsing extra_details:', error)
            existingExtraDetails = {}
          }
        }
        
        // Prepare new assignment data
        const newAssignmentData = {}
        
        if (this.bulkAssignmentForm.business) {
          const selectedBusiness = this.businessOptions.find(b => b.id === this.bulkAssignmentForm.business)
          newAssignmentData.business = selectedBusiness ? selectedBusiness.title : this.bulkAssignmentForm.business
          newAssignmentData.subBusiness = this.bulkAssignmentForm.subBusiness.map(id => {
            const subBusiness = this.subBusinessOptions.find(sb => sb.id === id)
            return subBusiness ? subBusiness.title : id
          })
        }
        
        if (this.bulkAssignmentForm.poc) {
          const selectedPOC = this.pocOptions.find(p => p.id === this.bulkAssignmentForm.poc)
          newAssignmentData.poc = selectedPOC ? selectedPOC.title : this.bulkAssignmentForm.poc
        }
        
        newAssignmentData.assignedBy = user
        newAssignmentData.assignedAt = new Date().toISOString()
        
        // Merge existing and new data (append, don't override)
        const mergedExtraDetails = {
          ...existingExtraDetails,
          ...newAssignmentData
        }
        
        const payload = {
          candidateStatus: candidate.candidateStatus,
          updatedBy: user,
          extra_details: mergedExtraDetails
        }
        
        return candidateService.updateCandidateStatus(candidate.cid, candidate.jid, payload)
          .then(response => {
            if (response.status === 'OK') {
              successCount++
            } else {
              errorCount++
            }
          })
          .catch(error => {
            console.error('Error updating candidate:', error)
            errorCount++
          })
      })
      
      Promise.all(promises).then(() => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: `Bulk assignment completed: ${successCount} successful, ${errorCount} failed`,
            icon: successCount > 0 ? 'CheckCircleIcon' : 'AlertCircleIcon',
            variant: errorCount === 0 ? 'success' : 'warning',
          },
        })
        
        // Refresh candidates data
        this.loadCandidates()
        
        // Close modal and clear selection
        this.$bvModal.hide('bulk-assignment-modal')
        this.resetBulkAssignmentForm()
        this.clearSelection()
      })
    },
    
    // Form reset methods
         resetAssignmentForm() {
       this.assignmentForm = {
         business: null,
         subBusiness: [],
         poc: null
       }
     },
     
         resetBulkAssignmentForm() {
      this.bulkAssignmentForm = {
        business: null,
        subBusiness: [],
        poc: null
      }
    },
    
    // Helper method to get current user's POC role
    getCurrentUserPOCRole() {
      const userPOC = this.pocOptions.find(poc => 
        poc.title.includes(this.currentUserEmpId)
      )
      return userPOC ? userPOC.abbrev : null
    },
    
    // Helper method to check if user can view all candidates
    canViewAllCandidates() {
      return this.canAssignBusinessAndPOC
    },
    
    // Helper method to get current user's full name for POC assignment comparison
    getCurrentUserFullName() {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      return `${userData.emp_id} - ${userData.first_name} ${userData.last_name}`
    },
    
    // Helper method to get current offer process status
    getCurrentOfferProcessStatus(candidate) {
      try {
        // If no offerProcess JSON exists, mark as "Not Started"
        if (!candidate.offerProcess) {
          return 'Not Started'
        }
        
        const offerProcess = typeof candidate.offerProcess === 'string' 
          ? JSON.parse(candidate.offerProcess) 
          : candidate.offerProcess
        
        // Check if tagging is completed (Tagged + ReqId)
        const isTaggingCompleted = () => {
          const taggingData = offerProcess.tagging
          if (!taggingData) return false
          return taggingData.status === 'Tagged' && taggingData.reqId && taggingData.reqId.trim() !== ''
        }
        
        // If tagging is not completed, show tagging status
        if (!isTaggingCompleted()) {
          const taggingData = offerProcess.tagging
          if (taggingData) {
            return `Tagging: ${taggingData.status || 'Pending'}`
          }
          return 'Tagging: Pending'
        }
        
        // Define the stage order for sequential progression
        const stageOrder = [
          { key: 'tagging', name: 'Tagging' },
          { key: 'jobApplication', name: 'Job Application' },
          { key: 'caf', name: 'CAF' },
          { key: 'documentation', name: 'Documentation' },
          { key: 'taf', name: 'TAF' },
          { key: 'haf', name: 'HAF' },
          { key: 'hrDiscussion', name: 'HR Discussion' },
          { key: 'fitment', name: 'Fitment' },
          { key: 'offerDesk', name: 'OfferDesk' },
          { key: 'kgsBGV', name: 'KGS BGV' }
        ]
        
        // Find the current stage (first incomplete stage)
        for (const stage of stageOrder) {
          const stageData = offerProcess[stage.key]
          
          // Skip HR Discussion in the main loop as it can be completed anytime
          if (stage.key === 'hrDiscussion') continue
          
          if (stageData) {
            // For stages after tagging, check if previous stages are completed
            if (stage.key === 'jobApplication') {
              // Job Application requires tagging to be completed
              if (!isTaggingCompleted()) {
                return `Tagging: ${offerProcess.tagging?.status || 'Pending'}`
              }
              if (stageData.status !== 'Completed') {
                return `${stage.name}: ${stageData.status || 'Pending'}`
              }
            } else if (stage.key === 'caf') {
              // CAF requires tagging + job application to be completed
              if (!isTaggingCompleted() || offerProcess.jobApplication?.status !== 'Completed') {
                return `Job Application: ${offerProcess.jobApplication?.status || 'Pending'}`
              }
              if (stageData.status !== 'Completed') {
                return `${stage.name}: ${stageData.status || 'Pending'}`
              }
            } else if (stage.key === 'documentation') {
              // Documentation requires all previous stages
              if (!isTaggingCompleted() || 
                  offerProcess.jobApplication?.status !== 'Completed' ||
                  offerProcess.caf?.status !== 'Completed') {
                return `CAF: ${offerProcess.caf?.status || 'Pending'}`
              }
              if (stageData.status !== 'Completed') {
                return `${stage.name}: ${stageData.status || 'Pending'}`
              }
            } else if (stage.key === 'taf') {
              // TAF requires all previous stages
              if (!isTaggingCompleted() || 
                  offerProcess.jobApplication?.status !== 'Completed' ||
                  offerProcess.caf?.status !== 'Completed' ||
                  offerProcess.documentation?.status !== 'Completed') {
                return `Documentation: ${offerProcess.documentation?.status || 'Pending'}`
              }
              if (stageData.status !== 'Completed') {
                return `${stage.name}: ${stageData.status || 'Pending'}`
              }
            } else if (stage.key === 'haf') {
              // HAF requires all previous stages
              if (!isTaggingCompleted() || 
                  offerProcess.jobApplication?.status !== 'Completed' ||
                  offerProcess.caf?.status !== 'Completed' ||
                  offerProcess.documentation?.status !== 'Completed' ||
                  offerProcess.taf?.status !== 'Completed') {
                return `TAF: ${offerProcess.taf?.status || 'Pending'}`
              }
              if (stageData.status !== 'Completed') {
                return `${stage.name}: ${stageData.status || 'Pending'}`
              }
            } else if (stage.key === 'fitment') {
              // Fitment requires all previous stages + HR Discussion
              if (!isTaggingCompleted() || 
                  offerProcess.jobApplication?.status !== 'Completed' ||
                  offerProcess.caf?.status !== 'Completed' ||
                  offerProcess.documentation?.status !== 'Completed' ||
                  offerProcess.taf?.status !== 'Completed' ||
                  offerProcess.haf?.status !== 'Completed' ||
                  (offerProcess.hrDiscussion?.status !== 'Completed' && offerProcess.hrDiscussion?.status !== 'Rejected')) {
                return `HAF: ${offerProcess.haf?.status || 'Pending'}`
              }
              if (stageData.status !== 'Completed') {
                return `${stage.name}: ${stageData.status || 'Pending'}`
              }
            } else if (stage.key === 'offerDesk') {
              // OfferDesk requires all previous stages + HR Discussion + Fitment
              if (!isTaggingCompleted() || 
                  offerProcess.jobApplication?.status !== 'Completed' ||
                  offerProcess.caf?.status !== 'Completed' ||
                  offerProcess.documentation?.status !== 'Completed' ||
                  offerProcess.taf?.status !== 'Completed' ||
                  offerProcess.haf?.status !== 'Completed' ||
                  (offerProcess.hrDiscussion?.status !== 'Completed' && offerProcess.hrDiscussion?.status !== 'Rejected') ||
                  offerProcess.fitment?.status !== 'Completed') {
                return `Fitment: ${offerProcess.fitment?.status || 'Pending'}`
              }
              if (stageData.status !== 'Extended') {
                return `${stage.name}: ${stageData.status || 'Pending'}`
              }
            } else if (stage.key === 'kgsBGV') {
              // KGS BGV requires all previous stages + OfferDesk Extended
              if (!isTaggingCompleted() || 
                  offerProcess.jobApplication?.status !== 'Completed' ||
                  offerProcess.caf?.status !== 'Completed' ||
                  offerProcess.documentation?.status !== 'Completed' ||
                  offerProcess.taf?.status !== 'Completed' ||
                  offerProcess.haf?.status !== 'Completed' ||
                  (offerProcess.hrDiscussion?.status !== 'Completed' && offerProcess.hrDiscussion?.status !== 'Rejected') ||
                  offerProcess.fitment?.status !== 'Completed' ||
                  offerProcess.offerDesk?.status !== 'Extended') {
                return `OfferDesk: ${offerProcess.offerDesk?.status || 'Pending'}`
              }
              if (stageData.status !== 'Cleared' && stageData.status !== 'Failed') {
                return `${stage.name}: ${stageData.status || 'Pending'}`
              }
            }
          } else {
            // Stage data doesn't exist, show as pending
            return `${stage.name}: Pending`
          }
        }
        
        // Check HR Discussion status separately (can be completed anytime)
        const hrDiscussionData = offerProcess.hrDiscussion
        if (hrDiscussionData && hrDiscussionData.status !== 'Completed' && hrDiscussionData.status !== 'Rejected') {
          return `HR Discussion: ${hrDiscussionData.status || 'Pending'}`
        }
        
        // If all stages are completed, check final status
        if (offerProcess.offerDesk?.status === 'Extended') {
          if (offerProcess.kgsBGV?.status === 'Cleared') {
            return 'KGS BGV: Cleared'
          } else if (offerProcess.kgsBGV?.status === 'Failed') {
            return 'KGS BGV: Failed'
          } else {
            return 'KGS BGV: Pending'
          }
        }
        
        // If we reach here, all stages are completed
        return 'Completed'
        
      } catch (error) {
        console.error('Error parsing offer process:', error)
        return 'Not Started'
      }
    },
    
    // Helper method to get last updated timestamp for current stage
    getLastUpdatedAt(candidate) {
      try {
        if (!candidate.offerProcess) {
          return null
        }
        
        const offerProcess = typeof candidate.offerProcess === 'string' 
          ? JSON.parse(candidate.offerProcess) 
          : candidate.offerProcess
        
        // Get current stage name from status
        const currentStatus = this.getCurrentOfferProcessStatus(candidate)
        const stageName = currentStatus.split(':')[0]
        
        // Map stage names to keys
        const stageKeyMap = {
          'Tagging': 'tagging',
          'Job Application': 'jobApplication',
          'CAF': 'caf',
          'Documentation': 'documentation',
          'TAF': 'taf',
          'HAF': 'haf',
          'HR Discussion': 'hrDiscussion',
          'Fitment': 'fitment',
          'OfferDesk': 'offerDesk',
          'KGS BGV': 'kgsBGV'
        }
        
        const stageKey = stageKeyMap[stageName]
        if (stageKey && offerProcess[stageKey] && offerProcess[stageKey].updatedAt) {
          return this.formatDate(offerProcess[stageKey].updatedAt)
        }
        
        return null
      } catch (error) {
        console.error('Error getting last updated at:', error)
        return null
      }
    },
    
    // Helper method to get tooltip content for offer process stage
    getOfferProcessTooltip(candidate) {
      try {
        if (!candidate.offerProcess) {
          return 'Offer process not started'
        }
        
        const offerProcess = typeof candidate.offerProcess === 'string' 
          ? JSON.parse(candidate.offerProcess) 
          : candidate.offerProcess
        
        // Get current stage name from status
        const currentStatus = this.getCurrentOfferProcessStatus(candidate)
        const stageName = currentStatus.split(':')[0]
        
        // Map stage names to keys
        const stageKeyMap = {
          'Tagging': 'tagging',
          'Job Application': 'jobApplication',
          'CAF': 'caf',
          'Documentation': 'documentation',
          'TAF': 'taf',
          'HAF': 'haf',
          'HR Discussion': 'hrDiscussion',
          'Fitment': 'fitment',
          'OfferDesk': 'offerDesk',
          'KGS BGV': 'kgsBGV'
        }
        
        const stageKey = stageKeyMap[stageName]
        if (stageKey && offerProcess[stageKey]) {
          const stageData = offerProcess[stageKey]
          let tooltip = `Stage: ${stageName}\nStatus: ${stageData.status || 'Pending'}`
          
          // Add ReqId for tagging stage
          if (stageKey === 'tagging' && stageData.reqId) {
            tooltip += `\nReqId: ${stageData.reqId}`
          }
          
          // Add comments if available
          if (stageData.comments && stageData.comments.length > 0) {
            tooltip += `\n\nComments:\n${stageData.comments.map(comment => 
              `• ${comment.text || comment} (${this.formatDate(comment.timestamp || comment.createdAt)})`
            ).join('\n')}`
          }
          
          // Add last updated info
          if (stageData.updatedAt) {
            tooltip += `\n\nLast Updated: ${this.formatDate(stageData.updatedAt)}`
            if (stageData.updatedBy) {
              tooltip += `\nUpdated By: ${stageData.updatedBy}`
            }
          }
          
          return tooltip
        }
        
        return currentStatus
      } catch (error) {
        console.error('Error getting tooltip:', error)
        return 'Error loading details'
      }
    },
    
    getAssignedPOCName(candidate) {
      try {
        // Only process extra_details for KPMG RPO candidates
        if (!candidate.extra_details) {
          return 'N/A'
        }
        
        const extraDetails = typeof candidate.extra_details === 'string' 
          ? JSON.parse(candidate.extra_details) 
          : candidate.extra_details
        
        if (extraDetails && extraDetails.poc) {
          // Remove emp_id from POC name (format: "emp_id - first_name last_name")
          const pocName = extraDetails.poc.replace(/^[A-Z]+-\d+\s*-\s*/, '').trim()
          return pocName || 'N/A'
        }
        
        return 'N/A'
      } catch (error) {
        console.error('Error parsing extra details:', error)
        return 'N/A'
      }
    },
    
    // Debug method to log POC assignment details
    debugPOCAssignment() {
      const userRole = this.getCurrentUserPOCRole()
      const canAssign = this.canAssignBusinessAndPOC
      
      console.log('POC Assignment Debug:', {
        userRole,
        canAssign,
        totalCandidates: this.candidates.length,
        assignedCandidates: this.assignedCandidates.length
      })
    },
    
    // Get stage tooltip for POC allocation table
    getStageTooltip(poc, stageKey) {
      const stage = poc.stages[stageKey]
      if (!stage) return 'No data'
      
      let tooltip = `POC: ${poc.name} (${poc.role})\nStage: ${this.getStageDisplayName(stageKey)}\nCount: ${stage.count}\nStatus: ${stage.status}`
      
      if (stage.comment) {
        tooltip += `\n\nComment: ${stage.comment}`
      }
      
      return tooltip
    },
    
    // Get stage display name
    getStageDisplayName(stageKey) {
      const stageMap = {
        tagging: 'Tagging',
        jobApplication: 'Job Application',
        caf: 'CAF',
        documentation: 'Documentation',
        haf: 'HAF',
        taf: 'TAF',
        hrDiscussion: 'HR Discussion',
        fitment: 'Fitment',
        offerDesk: 'OfferDesk',
        kgsBGV: 'KGS BGV',
        clientBGV: 'Client BGV'
      }
      return stageMap[stageKey] || stageKey
    },
    
    // Calculate total for a specific stage across all POCs
    getStageTotal(stageKey) {
      const pocAllocationData = this.calculatePOCAllocationData()
      return pocAllocationData.reduce((total, poc) => {
        return total + (poc.stages[stageKey]?.count || 0)
      }, 0)
    },
    
    // Calculate grand total across all POCs
    getGrandTotal() {
      const pocAllocationData = this.calculatePOCAllocationData()
      return pocAllocationData.reduce((total, poc) => {
        return total + poc.totalCandidates
      }, 0)
    },
    
    // Get total assigned candidates
    getTotalAssignedCandidates() {
      return this.getGrandTotal()
    },
    
    // Get unassigned candidates count
    getUnassignedCandidates() {
      const totalCandidates = this.totalCandidates || 0
      const assignedCandidates = this.getTotalAssignedCandidates()
      return Math.max(0, totalCandidates - assignedCandidates)
    },
    
    // Calculate active candidates for a POC (candidates not in final stages)
    calculateActiveCandidates(poc) {
      // Calculate based on stages that are not completed
      let activeCount = 0
      Object.values(poc.stages).forEach(stage => {
        if (stage.status !== 'Completed' && stage.status !== 'Cleared' && stage.status !== 'Extended') {
          activeCount += stage.count
        }
      })
      return activeCount
    },
    
    // Get POC variant color based on role
    getPOCVariant(role) {
      const variants = {
        'PRIMARY': 'primary',
        'SECONDARY': 'info',
        'BKP': 'success'
      }
      return variants[role] || 'secondary'
    },
    
    // Get percentage color for progress bars
    getPercentageColor(percentage) {
      if (percentage >= 70) return '#28a745' // Green
      if (percentage >= 40) return '#ffc107' // Yellow
      if (percentage >= 20) return '#fd7e14' // Orange
      return '#dc3545' // Red
    },
    
    // Get total active candidates
    getTotalActiveCandidates() {
      const pocAllocationData = this.calculatePOCAllocationData()
      return pocAllocationData.reduce((total, poc) => {
        return total + this.calculateActiveCandidates(poc)
      }, 0)
    },
    
    // Business summary helper methods
    getTotalBusinessAssigned() {
      const businessSummaryData = this.calculateBusinessSummaryData()
      return businessSummaryData.reduce((total, business) => {
        return total + business.assigned
      }, 0)
    },
    
    getTotalBusinessInProgress() {
      const businessSummaryData = this.calculateBusinessSummaryData()
      return businessSummaryData.reduce((total, business) => {
        return total + business.inProgress
      }, 0)
    },
    
    getTotalBusinessCompleted() {
      const businessSummaryData = this.calculateBusinessSummaryData()
      return businessSummaryData.reduce((total, business) => {
        return total + business.completed
      }, 0)
    },
    
    // Calculate Stage-wise Counts from actual data
    calculateStageWiseCounts() {
      const stageCounts = {}
      const totalCandidates = this.allCandidates.length
      
      // Initialize all stages with 0 count
      this.offerProcessStages.forEach(stage => {
        stageCounts[stage.key] = 0
      })
      
      // Count candidates in each stage
      this.allCandidates.forEach(candidate => {
        const currentStage = this.getCurrentOfferProcessStatus(candidate)
        if (currentStage && currentStage !== 'Not Started') {
          const stageName = currentStage.split(':')[0]
          const stageKey = this.getStageKeyFromName(stageName)
          if (stageKey) {
            stageCounts[stageKey] = (stageCounts[stageKey] || 0) + 1
          }
        }
      })
      
      // Convert to array format with percentages
      return this.offerProcessStages.map(stage => {
        const count = stageCounts[stage.key] || 0
        const percentage = totalCandidates > 0 ? Math.round((count / totalCandidates) * 100) : 0
        return {
          name: stage.name,
          count: count,
          percentage: percentage,
          variant: this.getStageVariant(stage.key)
        }
      })
    },
    
    // Calculate POC Allocation Data from actual data
    calculatePOCAllocationData() {
      const pocData = {}
      
      // Initialize POC data structure
      this.pocOptions.forEach(poc => {
        pocData[poc.title] = {
          name: poc.name || poc.title.split(' - ')[1] || poc.title,
          role: poc.abbrev || 'SECONDARY',
          totalCandidates: 0,
          stages: {}
        }
        
        // Initialize all stages for this POC
        this.offerProcessStages.forEach(stage => {
          pocData[poc.title].stages[stage.key] = {
            count: 0,
            status: 'Pending',
            comment: null
          }
        })
      })
      
      // Process each candidate
      this.allCandidates.forEach(candidate => {
        // Get assigned POC from extra_details
        const assignedPOC = this.getAssignedPOCName(candidate)
        const pocTitle = this.getPOCTitleFromName(assignedPOC)
        
        if (pocTitle && pocData[pocTitle]) {
          pocData[pocTitle].totalCandidates++
          
          // Get current stage
          const currentStage = this.getCurrentOfferProcessStatus(candidate)
          if (currentStage && currentStage !== 'Not Started') {
            const stageName = currentStage.split(':')[0]
            const stageKey = this.getStageKeyFromName(stageName)
            const stageStatus = currentStage.split(':')[1]?.trim() || 'Pending'
            
            if (stageKey && pocData[pocTitle].stages[stageKey]) {
              pocData[pocTitle].stages[stageKey].count++
              pocData[pocTitle].stages[stageKey].status = stageStatus
              
              // Add comment if available from offer process
              const offerProcess = this.parseOfferProcess(candidate.offerProcess)
              if (offerProcess && offerProcess[stageKey] && offerProcess[stageKey].comments) {
                pocData[pocTitle].stages[stageKey].comment = 
                  offerProcess[stageKey].comments[offerProcess[stageKey].comments.length - 1]?.text || null
              }
            }
          }
        }
      })
      
      // Convert to array format
      return Object.values(pocData).filter(poc => poc.totalCandidates > 0)
    },
    
    // Calculate Business Summary Data from actual data (Main Business Only)
    calculateBusinessSummaryData() {
      const businessData = {}
      
      // Get main business names from businessOptions
      const mainBusinessNames = this.businessOptions.map(business => business.title)
      
      // Process each candidate
      this.allCandidates.forEach(candidate => {
        // Get business from extra_details or candidate data
        let business = 'Unassigned'
        if (candidate.extra_details) {
          try {
            const extraDetails = typeof candidate.extra_details === 'string' 
              ? JSON.parse(candidate.extra_details) 
              : candidate.extra_details
            business = extraDetails.business || candidate.business || 'Unassigned'
          } catch (error) {
            business = candidate.business || 'Unassigned'
          }
        } else {
          business = candidate.business || 'Unassigned'
        }
        
        // Filter to only show main business names, not sub-business names
        // If the business name is not in mainBusinessNames, categorize as 'Unassigned'
        if (!mainBusinessNames.includes(business)) {
          business = 'Unassigned'
        }
        
        // Initialize business data if not exists
        if (!businessData[business]) {
          businessData[business] = {
            name: business,
            assigned: 0,
            inProgress: 0,
            completed: 0
          }
        }
        
        businessData[business].assigned++
        
        // Determine if candidate is in progress or completed
        const currentStage = this.getCurrentOfferProcessStatus(candidate)
        if (currentStage === 'Completed' || currentStage.includes('KGS BGV: Cleared')) {
          businessData[business].completed++
        } else {
          businessData[business].inProgress++
        }
      })
      
      // Convert to array format and sort by assigned count (descending)
      return Object.values(businessData).sort((a, b) => b.assigned - a.assigned)
    },
    
    // Helper method to get stage key from stage name
    getStageKeyFromName(stageName) {
      const stageMap = {
        'Tagging': 'tagging',
        'Job Application': 'jobApplication',
        'CAF': 'caf',
        'Documentation': 'documentation',
        'HAF': 'haf',
        'TAF': 'taf',
        'HR Discussion': 'hrDiscussion',
        'Fitment': 'fitment',
        'OfferDesk': 'offerDesk',
        'KGS BGV': 'kgsBGV',
        'Client BGV': 'clientBGV'
      }
      return stageMap[stageName]
    },
    
    // Helper method to get POC title from POC name
    getPOCTitleFromName(pocName) {
      if (pocName === 'N/A') return null
      
      // Find POC in pocOptions that matches the name
      const poc = this.pocOptions.find(poc => 
        poc.title.includes(pocName) || poc.name === pocName
      )
      return poc ? poc.title : null
    },
    
    // Helper method to get full POC title for filtering
    getFullPOCTitle(pocName) {
      if (pocName === 'N/A' || !pocName) return null
      
      // Find POC in pocOptions that matches the name
      const poc = this.pocOptions.find(poc => 
        poc.title.includes(pocName) || poc.name === pocName || poc.title === pocName
      )
      return poc ? poc.title : pocName
    },
    
    // Helper method to check if candidate has POC assigned
    hasPOCAssigned(candidate) {
      try {
        if (!candidate.extra_details) {
          return false
        }
        
        const extraDetails = typeof candidate.extra_details === 'string' 
          ? JSON.parse(candidate.extra_details) 
          : candidate.extra_details
        
        return extraDetails && extraDetails.poc && extraDetails.poc.trim() !== ''
      } catch (error) {
        console.error('Error checking POC assignment:', error)
        return false
      }
    },
    
    // Helper method to parse offer process JSON
    parseOfferProcess(offerProcess) {
      if (!offerProcess) return null
      
      try {
        return typeof offerProcess === 'string' 
          ? JSON.parse(offerProcess) 
          : offerProcess
      } catch (error) {
        console.error('Error parsing offer process:', error)
        return null
      }
    },
    
    // Helper method to get stage variant color
    getStageVariant(stageKey) {
      const variants = {
        'tagging': 'warning',
        'jobApplication': 'info',
        'caf': 'primary',
        'documentation': 'secondary',
        'haf': 'light',
        'taf': 'dark',
        'hrDiscussion': 'success',
        'fitment': 'danger',
        'offerDesk': 'warning',
        'kgsBGV': 'info',
        'clientBGV': 'primary'
      }
      return variants[stageKey] || 'primary'
    },
    
    // Excel Report Download
    async downloadExcelReport() {
      try {
        // Show loading state
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Generating Report',
            text: 'Please wait while we prepare your Excel report...',
            icon: 'LoaderIcon',
            variant: 'info',
          },
        })
        
        // Fetch all candidates for the report (without pagination)
        const allCandidates = await this.getAllCandidatesForReport()
        
        // Create workbook
        const workbook = XLSX.utils.book_new()
        
        // Sheet 1: Offer Process Stage Analysis
        const analysisData = this.prepareAnalysisDataForExcel(allCandidates)
        const analysisWorksheet = XLSX.utils.json_to_sheet(analysisData)
        XLSX.utils.book_append_sheet(workbook, analysisWorksheet, 'Offer Process Analysis')
        
        // Sheet 2: Candidate Detailed Data
        const candidateData = this.prepareCandidateDataForExcel(allCandidates)
        const candidateWorksheet = XLSX.utils.json_to_sheet(candidateData)
        XLSX.utils.book_append_sheet(workbook, candidateWorksheet, 'Candidate Details')
        
        // Sheet 3: Detailed Business Breakdown (including Sub Business)
        const detailedBusinessData = this.prepareDetailedBusinessDataForExcel(allCandidates)
        const detailedBusinessWorksheet = XLSX.utils.json_to_sheet(detailedBusinessData)
        XLSX.utils.book_append_sheet(workbook, detailedBusinessWorksheet, 'Business Details')
        
        // Generate filename with timestamp
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
        const filename = `KPMG_RPO_Report_${timestamp}.xlsx`
        
        // Download file
        XLSX.writeFile(workbook, filename)
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Report Downloaded Successfully',
            text: `Excel report "${filename}" has been downloaded with detailed business breakdown`,
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
        
      } catch (error) {
        console.error('Error downloading Excel report:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Download Failed',
            text: 'Error generating Excel report. Please try again.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    
    // Get all candidates for Excel report (without pagination)
    async getAllCandidatesForReport() {
      try {
        // Get user data from localStorage
        const userData = JSON.parse(localStorage.getItem('userData'))
        const emp_id = userData.emp_id
        const userPOC = userData.emp_id + ' - ' + userData.first_name + ' ' + userData.last_name

        const isPOC = this.pocOptions.some(poc => 
          poc.title.includes(userPOC)
        )
        
        // Prepare status parameter
        let statusParam = null
        if (this.filters.status && this.filters.status.length > 0) {
          if (this.filters.status.includes('')) {
            statusParam = this.statusOptions
              .filter(option => option.value !== '')
              .map(option => option.value)
          } else {
            statusParam = this.filters.status
          }
        }
        
        // Prepare parameters for getting all candidates
        const params = {
          page: 1,
          limit: 10000, // Large limit to get all candidates for Excel report
          business: this.filters.business || null,
          subBusiness: this.filters.subBusiness || null,
          status: statusParam,
          poc: this.filters.poc || null,
          emp_id: isPOC ? null : emp_id,
          filter: this.filters.search || null
        }
        
        console.log('Excel Report API Parameters:', params)
        
        const response = await candidateService.getKPMGRPOCandidates(params)
        
        if (response.status === "OK") {
          return response.data.candidates || []
        } else {
          throw new Error('Failed to fetch candidates for report')
        }
      } catch (error) {
        console.error('Error fetching all candidates for report:', error)
        throw error
      }
    },
    
    // Prepare analysis data for Excel
    prepareAnalysisDataForExcel(allCandidates = []) {
      const data = []
      
      // Add Stage-wise Candidate Count
      data.push(['STAGE-WISE CANDIDATE COUNT'])
      data.push(['Stage', 'Count', 'Percentage'])
      this.stageWiseCounts.forEach(stage => {
        data.push([stage.name, stage.count, `${stage.percentage}%`])
      })
      data.push([]) // Empty row
      
      // Add POC Assignment Summary
      data.push(['POC ASSIGNMENT SUMMARY'])
      data.push(['POC Name', 'Assigned', 'In Progress', 'Completed'])
      this.pocSummaryData.forEach(poc => {
        data.push([poc.name, poc.totalCandidates, poc.activeCandidates, poc.totalCandidates - poc.activeCandidates])
      })
      data.push(['Total', this.getTotalAssignedCandidates(), this.getTotalActiveCandidates(), this.getTotalAssignedCandidates() - this.getTotalActiveCandidates()])
      data.push([]) // Empty row
      
      // Add Business Wise Count (Main Business Only)
      data.push(['BUSINESS WISE COUNT (MAIN BUSINESS ONLY)'])
      data.push(['Note: Detailed Sub Business breakdown available in "Business Details" sheet'])
      data.push(['Business', 'Assigned', 'In Progress', 'Completed'])
      this.businessSummaryData.forEach(business => {
        data.push([business.name, business.assigned, business.inProgress, business.completed])
      })
      data.push(['Total', this.getTotalBusinessAssigned(), this.getTotalBusinessInProgress(), this.getTotalBusinessCompleted()])
      
      return data
    },
    
    // Prepare candidate data for Excel
    prepareCandidateDataForExcel(allCandidates = []) {
      const data = []
      
      // Get all unique extra_details keys from all candidates
      const allExtraDetailsKeys = new Set()
      allCandidates.forEach(candidate => {
        if (candidate.extra_details) {
          try {
            const extraDetails = typeof candidate.extra_details === 'string' 
              ? JSON.parse(candidate.extra_details) 
              : candidate.extra_details
            Object.keys(extraDetails).forEach(key => allExtraDetailsKeys.add(key))
          } catch (error) {
            console.error('Error parsing extra_details:', error)
          }
        }
      })
      
      // Get all offer process stage names
      const offerProcessStages = [
        'tagging',
        'jobApplication', 
        'caf',
        'documentation',
        'taf',
        'haf',
        'hrDiscussion',
        'fitment',
        'offerDesk',
        'kgsBGV',
        'offerAccepted',
        'clientBGV'
      ]
      
      // Build headers array
      const headers = [
        // Basic candidate details
        'Candidate ID',
        'Candidate Name',
        'Email',
        'Contact No',
        'Position',
        'Client',
        'Location',
        'Position Type/Role',
        'Vertical',
        'Category',
        'Payroll',
        'Joining Date',
        'Offered Date',
        'Offered CTC',
        'Notice Period',
        'Total Experience',
        'Current CTC',
        'Expected CTC',
        'Current Company',
        'Current Position/Designation',
        'Qualification',
        'Gender',
        'Date of Birth',
        'Current City/Location',
        'Preferred City/Location',
        'Key Skills/Technical Skills',
        'Address',
        'Pincode',
        'State',
        'Country',
        'Marital Status',
        'Languages Known',
        'Passport Number',
        'Aadhaar Number',
        'PAN Number',
        'UAN Number',
        'Bank Name',
        'Account Number',
        'IFSC Code',
        'Branch Name',
        'Emergency Contact Name',
        'Emergency Contact Number',
        'Emergency Contact Relation',
        'Reference 1 Name',
        'Reference 1 Company',
        'Reference 1 Designation',
        'Reference 1 Contact',
        'Reference 2 Name',
        'Reference 2 Company',
        'Reference 2 Designation',
        'Reference 2 Contact',
        
        // Recruiter and source details
        'Recruiter Name',
        'Recruiter Email',
        'Team Lead Name',
        'Team Lead Email',
        'Account Manager Name',
        'Account Manager Email',
        'Sourcer Name',
        'Sourcer Email',
        'Source',
        
        // Candidate status
        'Candidate Status',
        'Added On',
        'Updated On',
        
        // Extra details (all keys)
        ...Array.from(allExtraDetailsKeys).map(key => `Extra Details - ${key}`),
        
        // Offer process stages (all stages)
        ...offerProcessStages.map(stage => `${this.getStageDisplayName(stage)} Status`)
      ]
      
      data.push(headers)
      
      // Add candidate data
      allCandidates.forEach(candidate => {
        const offerProcess = candidate.offerProcess ? 
          (typeof candidate.offerProcess === 'string' ? JSON.parse(candidate.offerProcess) : candidate.offerProcess) : {}
        
        const extraDetails = candidate.extra_details ? 
          (typeof candidate.extra_details === 'string' ? JSON.parse(candidate.extra_details) : candidate.extra_details) : {}
        
        // Build row data
        const rowData = [
          // Basic candidate details
          candidate.candidate_id || candidate.cid || '',
          this.getCandidateName(candidate),
          candidate.email || '',
          candidate.contact_no || '',
          this.getPositionName(candidate),
          candidate.client || '',
          candidate.locations || candidate.current_location || candidate.currentLocation || '',
          candidate.position_type || candidate.role || '',
          candidate.vertical || '',
          candidate.category || '',
          candidate.payroll || '',
          candidate.joining_date || '',
          candidate.offered_date || '',
          candidate.offered_ctc || candidate.offeredCTC || '',
          candidate.notice_period || candidate.noticePeriod || '',
          candidate.experience || candidate.current_exp || candidate.currentExp || candidate.total_experience || candidate.totalExperience || '',
          candidate.current_ctc || candidate.currentCTC || candidate.current_ctc_amount || candidate.currentCtcAmount || '',
          candidate.expected_ctc || candidate.expectedCTC || candidate.expected_ctc_amount || candidate.expectedCtcAmount || '',
          candidate.current_company || candidate.currentCompany || candidate.current_employer || candidate.currentEmployer || '',
          candidate.current_position || candidate.currentPosition || candidate.current_designation || candidate.currentDesignation || '',
          candidate.qualification || '',
          candidate.gender || '',
          candidate.date_of_birth || candidate.dob || '',
          candidate.current_city || candidate.currentCity || candidate.current_location || candidate.currentLocation || '',
          candidate.preferred_city || candidate.preferredCity || candidate.preferred_location || candidate.preferredLocation || '',
          candidate.key_skills || candidate.skills || candidate.technical_skills || candidate.technicalSkills || '',
          candidate.address || '',
          candidate.pincode || '',
          candidate.state || '',
          candidate.country || '',
          candidate.marital_status || candidate.maritalStatus || '',
          candidate.languages_known || candidate.languagesKnown || '',
          candidate.passport_number || candidate.passportNumber || '',
          candidate.aadhaar_number || candidate.aadhaarNumber || '',
          candidate.pan_number || candidate.panNumber || '',
          candidate.uan_number || candidate.uanNumber || '',
          candidate.bank_name || candidate.bankName || '',
          candidate.account_number || candidate.accountNumber || '',
          candidate.ifsc_code || candidate.ifscCode || '',
          candidate.branch_name || candidate.branchName || '',
          candidate.emergency_contact_name || candidate.emergencyContactName || '',
          candidate.emergency_contact_number || candidate.emergencyContactNumber || '',
          candidate.emergency_contact_relation || candidate.emergencyContactRelation || '',
          candidate.reference1_name || candidate.reference1Name || '',
          candidate.reference1_company || candidate.reference1Company || '',
          candidate.reference1_designation || candidate.reference1Designation || '',
          candidate.reference1_contact || candidate.reference1Contact || '',
          candidate.reference2_name || candidate.reference2Name || '',
          candidate.reference2_company || candidate.reference2Company || '',
          candidate.reference2_designation || candidate.reference2Designation || '',
          candidate.reference2_contact || candidate.reference2Contact || '',
          
          // Recruiter and source details
          candidate.recruiter || candidate.recruiter_name || '',
          candidate.recruiter_email || '',
          candidate.team_lead || candidate.team_lead_name || '',
          candidate.team_lead_email || '',
          candidate.account_mgr || candidate.account_manager || '',
          candidate.account_mgr_email || candidate.account_manager_email || '',
          candidate.sourcer || candidate.sourcer_name || '',
          candidate.sourcer_email || '',
          candidate.source || '',
          
          // Candidate status
          candidate.candidateStatus || '',
          candidate.created_at || candidate.added_on || '',
          candidate.updated_at || '',
          
          // Extra details (all keys)
          ...Array.from(allExtraDetailsKeys).map(key => {
            const value = extraDetails[key]
            if (Array.isArray(value)) {
              return value.join(', ')
            } else if (typeof value === 'object' && value !== null) {
              return JSON.stringify(value)
            } else {
              return value || ''
            }
          }),
          
          // Offer process stages (all stages)
          ...offerProcessStages.map(stage => {
            const stageData = offerProcess[stage]
            return stageData ? (stageData.status || 'Not Started') : 'Not Started'
          })
        ]
        
        data.push(rowData)
      })
      
      return data
    },
    
    // Prepare detailed business data for Excel (including Sub Business breakdown)
    prepareDetailedBusinessDataForExcel(allCandidates = []) {
      const data = []
      
      // Add headers
      data.push([
        'Business',
        'Sub Business',
        'Total Assigned',
        'In Progress',
        'Completed',
        'Completion Rate (%)'
      ])
      
      // Group candidates by business and sub-business
      const businessSubBusinessData = {}
      
      allCandidates.forEach(candidate => {
        let business = 'Unassigned'
        let subBusiness = 'N/A'
        
        if (candidate.extra_details) {
          try {
            const extraDetails = typeof candidate.extra_details === 'string' 
              ? JSON.parse(candidate.extra_details) 
              : candidate.extra_details
            business = extraDetails.business || candidate.business || 'Unassigned'
            subBusiness = extraDetails.subBusiness ? 
              (Array.isArray(extraDetails.subBusiness) ? extraDetails.subBusiness.join(', ') : extraDetails.subBusiness) : 'N/A'
          } catch (error) {
            business = candidate.business || 'Unassigned'
            subBusiness = 'N/A'
          }
        } else {
          business = candidate.business || 'Unassigned'
          subBusiness = 'N/A'
        }
        
        const key = `${business}|${subBusiness}`
        
        if (!businessSubBusinessData[key]) {
          businessSubBusinessData[key] = {
            business: business,
            subBusiness: subBusiness,
            assigned: 0,
            inProgress: 0,
            completed: 0
          }
        }
        
        businessSubBusinessData[key].assigned++
        
        // Determine if candidate is in progress or completed
        const currentStage = this.getCurrentOfferProcessStatus(candidate)
        if (currentStage === 'Completed' || currentStage.includes('KGS BGV: Cleared')) {
          businessSubBusinessData[key].completed++
        } else {
          businessSubBusinessData[key].inProgress++
        }
      })
      
      // Convert to array and add completion rate
      Object.values(businessSubBusinessData).forEach(item => {
        const completionRate = item.assigned > 0 ? Math.round((item.completed / item.assigned) * 100) : 0
        data.push([
          item.business,
          item.subBusiness,
          item.assigned,
          item.inProgress,
          item.completed,
          completionRate
        ])
      })
      
      return data
    },
    
    // Show access level notification
    // showAccessLevelNotification() {
    //   const userRole = this.getCurrentUserPOCRole()
    //   const canAssign = this.canAssignBusinessAndPOC
      
    //   if (canAssign) {
    //     this.$toast({
    //       component: ToastificationContent,
    //       props: {
    //         title: 'Full Access Mode',
    //         text: 'You have full access to assign Business and POC to all candidates',
    //         icon: 'CheckCircleIcon',
    //         variant: 'success',
    //       },
    //     })
    //   } else if (userRole === 'SECONDARY') {
    //     this.$toast({
    //       component: ToastificationContent,
    //       props: {
    //         title: 'Assigned Candidates Only',
    //         text: 'You can only view candidates assigned to you',
    //         icon: 'InfoIcon',
    //         variant: 'info',
    //       },
    //     })
    //   } else {
    //     this.$toast({
    //       component: ToastificationContent,
    //       props: {
    //         title: 'Limited Access',
    //         text: 'You can only view candidates assigned to you',
    //         icon: 'AlertCircleIcon',
    //         variant: 'warning',
    //       },
    //     })
    //   }
    // }
  }
}
</script>

<style lang="scss" scoped>
@import 'vue-select/src/scss/vue-select.scss';
.kpmg-rpo-dashboard {
  padding: 1.5rem;

  // Access Denied Styling
  .access-denied-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60vh;
    padding: 2rem;
    
    .access-denied-content {
      text-align: center;
      max-width: 500px;
      padding: 3rem 2rem;
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
      border: 1px solid #e9ecef;
      
      .access-denied-icon {
        margin-bottom: 1.5rem;
        color: #dc3545;
      }
      
      .access-denied-title {
        color: #dc3545;
        font-weight: 600;
        margin-bottom: 1rem;
        font-size: 1.5rem;
      }
      
      .access-denied-message {
        color: #6c757d;
        font-size: 1.1rem;
        margin-bottom: 1rem;
        line-height: 1.5;
      }
      
      .access-denied-subtitle {
        color: #495057;
        font-size: 0.9rem;
        margin-bottom: 1.5rem;
        line-height: 1.4;
        
        code {
          background-color: #f8f9fa;
          padding: 0.2rem 0.4rem;
          border-radius: 4px;
          font-size: 0.85rem;
          color: #e83e8c;
        }
      }
    }
  }

  .dashboard-header {
    border-bottom: 2px solid #e9ecef;
    padding-bottom: 1rem;
  }

  .metric-card {
    border: none;
    border-radius: 12px;
    transition: all 0.3s ease;
    padding: 1.75rem;
    position: relative;
    overflow: hidden;
    min-height: 120px;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      opacity: 0.1;
      transition: opacity 0.3s ease;
    }

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      
      &::before {
        opacity: 0.05;
      }
      
      .metric-icon {
        transform: scale(1.05);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
      }
    }

    .metric-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 100%;
      position: relative;
      z-index: 1;
    }

    .metric-info {
      flex: 1;
    }

    .metric-icon {
      width: 64px;
      height: 64px;
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      position: relative;
      z-index: 2;
      background: white;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      border: 2px solid transparent;
      transition: all 0.3s ease;
      
      svg {
        width: 28px;
        height: 28px;
        stroke-width: 2;
      }
    }

    .metric-value {
      font-size: 2rem;
      font-weight: 700;
      margin: 0;
      line-height: 1.1;
      color: #4a5568;
      margin-bottom: 0.25rem;
    }

    .metric-label {
      margin: 0;
      color: #718096;
      font-size: 0.875rem;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    // Gradient variants
    &.primary-gradient {
      background: linear-gradient(135deg, #f8f9ff 0%, #e8ecff 100%);
      border: 1px solid #e3e8ff;
      
      .metric-icon {
        border-color: #667eea;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
        
        svg {
          color: #667eea;
        }
      }
      
      .metric-value {
        color: #4a5568;
      }
      
      .metric-label {
        color: #718096;
      }
    }

    &.success-gradient {
      background: linear-gradient(135deg, #f0fff4 0%, #e6fffa 100%);
      border: 1px solid #c6f6d5;
      
      .metric-icon {
        border-color: #48bb78;
        box-shadow: 0 4px 12px rgba(72, 187, 120, 0.15);
        
        svg {
          color: #48bb78;
        }
      }
      
      .metric-value {
        color: #4a5568;
      }
      
      .metric-label {
        color: #718096;
      }
    }

    &.info-gradient {
      background: linear-gradient(135deg, #f0f9ff 0%, #e6f3ff 100%);
      border: 1px solid #bee3f8;
      
      .metric-icon {
        border-color: #4299e1;
        box-shadow: 0 4px 12px rgba(66, 153, 225, 0.15);
        
        svg {
          color: #4299e1;
        }
      }
      
      .metric-value {
        color: #4a5568;
      }
      
      .metric-label {
        color: #718096;
      }
    }

    &.warning-gradient {
      background: linear-gradient(135deg, #fffbeb 0%, #fef5e7 100%);
      border: 1px solid #fed7aa;
      
      .metric-icon {
        border-color: #ed8936;
        box-shadow: 0 4px 12px rgba(237, 137, 54, 0.15);
        
        svg {
          color: #ed8936;
        }
      }
      
      .metric-value {
        color: #4a5568;
      }
      
      .metric-label {
        color: #718096;
      }
    }
  }

  .timeline-card,
  .analysis-card,
  .poc-summary-card {
    border: none;
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    
    .unassigned-badge {
      .unassigned-count-badge {
        font-size: 0.85rem;
        padding: 0.5rem 0.75rem;
        border-radius: 20px;
        background-color: #fff3cd;
        color: #856404;
        border: 1px solid #ffeaa7;
        
        &:hover {
          background-color: #ffeaa7;
          color: #856404;
        }
      }
    }

    .timeline-details {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 1rem;
    }

    .timeline-main-info {
      flex: 1;
      
      .candidate-name {
        font-weight: 600;
        color: #495057;
        margin-bottom: 0.25rem;
        font-size: 0.9rem;
      }
      
      .position-name {
        color: #6c757d;
        font-size: 0.8rem;
      }
    }

    .timeline-side-info {
      text-align: right;
      min-width: 80px;
      
      .status-info {
        margin-bottom: 0.25rem;
      }
      
      .recruiter-info {
        color: #6c757d;
        font-size: 0.75rem;
        margin-bottom: 0.25rem;
      }
      
      .update-time {
        font-size: 0.7rem;
      }
    }
  }

  .stats-card {
    border: none;
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

    .stats-list {
      .stat-item {
        display: flex;
        align-items: center;
        padding: 1rem 0;
        border-bottom: 1px solid #e9ecef;

        &:last-child {
          border-bottom: none;
        }

        .stat-icon {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.2rem;
          margin-right: 1rem;
        }

        .stat-content {
          h4 {
            margin: 0;
            font-size: 1.5rem;
            font-weight: bold;
            color: #495057;
          }

          p {
            margin: 0;
            color: #6c757d;
            font-size: 0.9rem;
          }
        }
      }
    }
  }

  .candidates-card {
    border: none;
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

    .search-container {
      position: relative;
      
      .search-feedback {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        text-align: center;
        margin-top: 2px;
        font-size: 0.75rem;
      }
      
      .form-control.is-invalid {
        border-color: #dc3545;
        box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
      }
    }

    .bulk-actions {
      background: #f8f9fa;
      padding: 1rem;
      border-radius: 8px;
      border: 1px solid #e9ecef;
      transition: all 0.3s ease;
      
      &.active {
        background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
        border: 1px solid #bbdefb;
        box-shadow: 0 2px 8px rgba(33, 150, 243, 0.1);
      }
      
      .selection-indicator {
        display: flex;
        align-items: center;
        font-weight: 600;
        color: #2e7d32;
        
        .selection-count {
          font-size: 0.95rem;
        }
      }
      
      .bulk-actions-placeholder {
        .text-muted {
          font-size: 0.9rem;
        }
      }
    }

    .table-controls {
      background: #f8f9fa;
      padding: 1rem;
      border-radius: 8px;
      border: 1px solid #e9ecef;
    }
    
    .table-header-info {
      background: #f8f9fa;
      padding: 0.75rem 1rem;
      border-radius: 6px;
      border: 1px solid #e9ecef;
      
      .selection-info {
        display: flex;
        align-items: center;
        font-size: 0.9rem;
      }
      
      .table-stats {
        font-size: 0.85rem;
      }
    }

    .candidates-table {
      // Ensure checkboxes are visible and styled
      .b-table-select-all {
        .custom-control {
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }
      }
      
      .b-table-select {
        .custom-control {
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }
      }
      
      // Enhanced selection styling
      tbody tr {
        transition: all 0.2s ease;
        
        &.table-primary {
          background-color: rgba(40, 167, 69, 0.1) !important;
          border-left: 4px solid #28a745;
          
          &:hover {
            background-color: rgba(40, 167, 69, 0.15) !important;
          }
        }
        
        &:hover {
          background-color: rgba(40, 167, 69, 0.05);
        }
      }
      
      // Checkbox styling
      .custom-control-input:checked ~ .custom-control-label::before {
        background-color: #28a745;
        border-color: #28a745;
      }
      
      .custom-control-input:focus ~ .custom-control-label::before {
        box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
      }
      
      // Force checkbox visibility
      .custom-control {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        position: relative !important;
      }
      
      .custom-control-input {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
      }
      
      .custom-control-label {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
      }
      
      .candidate-info {
        display: flex;
        align-items: center;
        padding: 0.25rem 0;
        
        .candidate-name {
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      .position-info {
        max-width: 200px;
        
        .position-name {
          font-weight: 600;
          color: #495057;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          margin-bottom: 0.25rem;
        }
        
        .position-details {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.25rem;
          
          .badge {
            font-size: 0.7rem;
            padding: 0.2rem 0.4rem;
          }
        }
      }

      .assigned-to-info {
        font-size: 0.85rem;
        color: #495057;
        font-weight: 500;
      }

      .offer-process-stage {
        font-size: 0.85rem;
        color: #6c757d;
        font-weight: 500;
      }
      
      .offer-process-stage-info {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        
        .stage-status {
          font-weight: 600;
          color: #495057;
          cursor: help;
          text-decoration: underline;
          text-decoration-style: dotted;
          text-underline-offset: 2px;
        }
        
        .last-updated {
          font-size: 0.75rem;
          color: #6c757d;
          font-style: italic;
        }
      }

      .status-indicator {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 80px;
      }

      .status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;

        &.status-dot-success { background: #28a745; }
        &.status-dot-info { background: #17a2b8; }
        &.status-dot-warning { background: #fd7e14; }
        &.status-dot-danger { background: #dc3545; }
        &.status-dot-secondary { background: #6c757d; }
        &.status-dot-primary { background: #007bff; }
      }

      .action-buttons {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
      }
    }
  }
  
  // Modal Styling
  .assignment-modal,
  .bulk-assignment-modal {
    .modal-content {
      border-radius: 8px;
      border: none;
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    }
    
    .modal-header {
      border-bottom: 1px solid #e9ecef;
      padding: 1rem 1.5rem 0.75rem;
      
      .modal-title {
        font-weight: 600;
        color: #495057;
        font-size: 1rem;
      }
    }
    
    .modal-body {
      padding: 1rem 1.5rem 1.5rem;
    }
  }
  
  .assignment-content,
  .bulk-assignment-content {
    .candidate-info {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0;
      border-bottom: 1px solid #e9ecef;
      
      .candidate-name {
        font-weight: 600;
        color: #495057;
        font-size: 0.9rem;
      }
      
      .candidate-position {
        color: #6c757d;
        font-size: 0.8rem;
        
        &::before {
          content: '-';
          margin: 0 0.25rem;
        }
      }
    }
    
    .selection-summary {
      display: flex;
      align-items: center;
      padding: 0.5rem 0;
      border-bottom: 1px solid #e9ecef;
      margin-bottom: 1rem;
      
      span {
        font-weight: 500;
        color: #495057;
        font-size: 0.9rem;
      }
    }
    
    .modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      padding-top: 1rem;
      margin-top: 1rem;
    }
    
    .selected-candidates-list {
      max-height: 120px;
      overflow-y: auto;
      border: 1px solid #e9ecef;
      border-radius: 4px;
      padding: 0.5rem;
      background-color: #f8f9fa;
      
      .candidate-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.25rem 0;
        border-bottom: 1px solid #dee2e6;
        
        &:last-child {
          border-bottom: none;
        }
        
        .candidate-name {
          font-weight: 500;
          color: #495057;
          font-size: 0.85rem;
        }
        
        .candidate-position {
          color: #6c757d;
          font-size: 0.8rem;
          
          &::before {
            content: '-';
            margin: 0 0.25rem;
          }
        }
      }
      
      .more-candidates {
        padding: 0.25rem 0;
        font-size: 0.8rem;
        text-align: center;
        color: #6c757d;
      }
    }
  }
  
  .selected-candidates-list {
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    padding: 0.5rem;
    background-color: #c0fcd1;
    
    .candidate-item {
      padding: 0.25rem 0;
      border-bottom: 1px solid #dee2e6;
      
      &:last-child {
        border-bottom: none;
      }
      
      .candidate-name {
        font-weight: 600;
        color: #495057;
      }
      
      .candidate-position {
        color: #6c757d;
        font-size: 0.875rem;
      }
    }
  }
  
  // Additional checkbox styling
  .table {
    .b-table-select-all,
    .b-table-select {
      .custom-control {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
      }
      
      .custom-control-input {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
      }
      
      .custom-control-label {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
      }
    }
  }
  
  // POC Summary Table styling
  .poc-summary-table-container {
    .poc-summary-table {
      font-size: 0.8rem;
      margin-bottom: 0;
      
      thead {
        th {
          background-color: #f8f9fa;
          border-bottom: 2px solid #dee2e6;
          font-weight: 600;
          color: #495057;
          text-align: center;
          padding: 0.5rem 0.25rem;
          font-size: 0.75rem;
          
          &.poc-name-header {
            text-align: left;
            min-width: 100px;
          }
          
          &.role-header {
            min-width: 80px;
          }
          
          &.assigned-header,
          &.active-header {
            min-width: 70px;
          }
          
          &.percentage-header {
            min-width: 60px;
          }
        }
      }
      
      tbody {
        tr {
          &:hover {
            background-color: rgba(0, 123, 255, 0.05);
          }
          
          &.total-row {
            background-color: #f8f9fa;
            border-top: 2px solid #dee2e6;
            
            td {
              font-weight: 600;
              color: #495057;
            }
          }
        }
        
        td {
          padding: 0.5rem 0.25rem;
          text-align: center;
          vertical-align: middle;
          border-bottom: 1px solid #f1f3f4;
          
          &.poc-name-cell {
            text-align: left;
            
            .poc-name {
              font-weight: 600;
              color: #495057;
              font-size: 0.85rem;
            }
          }
          
          &.role-cell {
            .role-badge {
              font-size: 0.7rem;
              padding: 0.2rem 0.4rem;
              border-radius: 8px;
              font-weight: 500;
              
              &.role-primary {
                background-color: #e3f2fd;
                color: #1976d2;
              }
              
              &.role-secondary {
                background-color: #e8f5e8;
                color: #2e7d32;
              }
              
              &.role-bkp {
                background-color: #fff3e0;
                color: #f57c00;
              }
            }
          }
          
                     &.assigned-cell,
           &.inprogress-cell,
           &.completed-cell {
             .count {
               font-weight: 600;
               color: #495057;
               font-size: 0.85rem;
             }
           }
        }
      }
    }
    
    .summary-footer {
      margin-top: 0.75rem;
      padding-top: 0.75rem;
      border-top: 1px solid #e9ecef;
      
      .unassigned-info {
        text-align: center;
        font-size: 0.75rem;
        
        small {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.25rem;
        }
      }
         }
   }
   
   // Business Summary Table styling
   .business-summary-table-container {
     .business-summary-table {
       font-size: 0.8rem;
       margin-bottom: 0;
       
       thead {
         th {
           background-color: #f8f9fa;
           border-bottom: 2px solid #dee2e6;
           font-weight: 600;
           color: #495057;
           text-align: center;
           padding: 0.5rem 0.25rem;
           font-size: 0.75rem;
           
           &.business-name-header {
             text-align: left;
             min-width: 100px;
           }
           
           &.assigned-header,
           &.inprogress-header,
           &.completed-header {
             min-width: 70px;
           }
         }
       }
       
       tbody {
         tr {
           &:hover {
             background-color: rgba(0, 123, 255, 0.05);
           }
           
           &.total-row {
             background-color: #f8f9fa;
             border-top: 2px solid #dee2e6;
             
             td {
               font-weight: 600;
               color: #495057;
             }
           }
         }
         
         td {
           padding: 0.5rem 0.25rem;
           text-align: center;
           vertical-align: middle;
           border-bottom: 1px solid #f1f3f4;
           
           &.business-name-cell {
             text-align: left;
             
             .business-name {
               font-weight: 600;
               color: #495057;
               font-size: 0.85rem;
             }
           }
           
           &.assigned-cell,
           &.inprogress-cell,
           &.completed-cell {
             .count {
               font-weight: 600;
               color: #495057;
               font-size: 0.85rem;
             }
           }
         }
       }
     }
   }
   
   // Analysis sections styling
  .analysis-section {
    margin-bottom: 1.5rem;
    
    .section-title {
      display: flex;
      align-items: center;
      font-weight: 600;
      color: #495057;
      margin-bottom: 1rem;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid #e9ecef;
    }
  }
  
  .stage-counts {
    .stage-count-item {
      margin-bottom: 1rem;
      
      .stage-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.5rem;
        
        .stage-name {
          font-weight: 500;
          color: #495057;
          font-size: 0.9rem;
        }
        
        .stage-count {
          font-weight: 600;
          color: #6c757d;
          font-size: 0.85rem;
        }
      }
      
      .progress-container {
        .stage-progress {
          border-radius: 4px;
        }
      }
    }
  }
  
  .poc-allocation-table-container {
    max-height: 400px;
    overflow-y: auto;
    
    .poc-allocation-table {
      font-size: 0.8rem;
      margin-bottom: 0;
      
      thead {
        th {
          background-color: #f8f9fa;
          border-bottom: 2px solid #dee2e6;
          font-weight: 600;
          color: #495057;
          text-align: center;
          padding: 0.5rem 0.25rem;
          font-size: 0.75rem;
          position: sticky;
          top: 0;
          z-index: 10;
          
          &.poc-name-header {
            text-align: left;
            min-width: 120px;
          }
          
          &.stage-header {
            min-width: 80px;
            max-width: 100px;
          }
          
          &.total-header {
            min-width: 60px;
            background-color: #e9ecef;
            font-weight: 700;
          }
        }
      }
      
      tbody {
        tr {
          &:hover {
            background-color: rgba(0, 123, 255, 0.05);
          }
          
          &.total-row {
            background-color: #f8f9fa;
            border-top: 2px solid #dee2e6;
            
            td {
              font-weight: 600;
              color: #495057;
            }
          }
        }
        
        td {
          padding: 0.5rem 0.25rem;
          text-align: center;
          vertical-align: middle;
          border-bottom: 1px solid #f1f3f4;
          
          &.poc-name-cell {
            text-align: left;
            
            .poc-info {
              display: flex;
              flex-direction: column;
              gap: 0.1rem;
              
              .poc-name {
                font-weight: 600;
                color: #495057;
                font-size: 0.85rem;
              }
              
              .poc-role {
                color: #6c757d;
                font-size: 0.7rem;
                background-color: #e9ecef;
                padding: 0.1rem 0.3rem;
                border-radius: 8px;
                display: inline-block;
                width: fit-content;
              }
            }
          }
          
          &.stage-cell {
            .stage-count {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 0.2rem;
              cursor: help;
              padding: 0.2rem 0.4rem;
              border-radius: 4px;
              transition: background-color 0.2s ease;
              
              &:hover {
                background-color: rgba(0, 123, 255, 0.1);
              }
              
              .count-number {
                font-weight: 600;
                color: #495057;
              }
              
              .comment-indicator {
                color: #007bff;
                display: flex;
                align-items: center;
              }
              
              &.zero-count {
                color: #6c757d;
                font-weight: 400;
              }
            }
          }
          
          &.total-cell {
            background-color: #f8f9fa;
            font-weight: 600;
            color: #495057;
          }
        }
      }
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .kpmg-rpo-dashboard {
    padding: 1rem;

    .metric-card {
      padding: 1.25rem;
      min-height: 100px;
      
      .metric-content {
        flex-direction: column;
        text-align: center;
        gap: 0.5rem;
      }

      .metric-info {
        text-align: center;
      }

      .metric-icon {
        width: 52px;
        height: 52px;
        border-radius: 12px;
        
        svg {
          width: 24px;
          height: 24px;
        }
      }

      .metric-value {
        font-size: 1.75rem;
      }

      .metric-label {
        font-size: 0.8rem;
      }
    }
  }
  
  // Custom v-select styling
  .v-select {
    .vs__dropdown-toggle {
      border: 1px solid #ced4da;
      border-radius: 0.375rem;
      background-color: #fff;
      min-height: 38px;
      
      &:focus-within {
        border-color: #80bdff;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
      }
    }
    
    .vs__selected-options {
      padding: 0.375rem 0.75rem;
    }
    
    .vs__actions {
      padding: 0.375rem 0.75rem;
    }
    
    .vs__dropdown-menu {
      border: 1px solid #ced4da;
      border-radius: 0.375rem;
      box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
      
      .vs__dropdown-option {
        padding: 0.5rem 0.75rem;
        
        &.vs__dropdown-option--highlight {
          background-color: #007bff;
          color: white;
        }
      }
    }
    
    .vs__search {
      padding: 0.375rem 0.75rem;
      border: none;
      outline: none;
      
      &::placeholder {
        color: #6c757d;
      }
    }
    
    .vs__clear {
      color: #6c757d;
      
      &:hover {
        color: #dc3545;
      }
    }
    
    .vs__open-indicator {
      color: #6c757d;
    }
  }
}
</style> 